import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  type Edge,
  type Node,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SETS_ENUM_TYPES, UID_NAME_TUPLES } from 'constants/dataSets';
import dayjs from 'dayjs';
import { useEnumQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums } from 'store/slices/dataSets/selectors';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';

import useMonitoringResults from '../../../../../components/MonitoringAgent/hooks/useMonitoringResults';
import buildMonitoringScheduleTitle from '../../../../../components/MonitoringAgent/utils/buildMonitoringScheduleTitle';
import {
  buildMonitoringImpactScenarioInitialData,
  shouldShowMonitoringImpactScenarioCta,
} from '../../../../../components/MonitoringAgent/utils/monitoringImpactBridgeUtils';
import { getAtRiskUnitIds } from '../../../../../components/MonitoringAgent/utils/monitoringResultsUtils';
import { normalizeTimeOfDay } from '../../../../../components/MonitoringAgent/utils/monitoringScheduleDateUtils';
import { resolveAIPanelSelectableUnitTypes } from '../../../../../components/MonitoringAgent/utils/monitoringSelectionUtils';
import {
  buildMonitoringStartPayload,
  isMonitoringSessionProcessingStatus,
  isMonitoringSessionReadyForResults,
  isMonitoringSessionType,
  logMonitoringSessionStartError,
  resolveMonitoringActiveAgentKind,
  resolveMonitoringSessionAfterStart,
  shouldUseMonitoringMock,
} from '../../../../../components/MonitoringAgent/utils/monitoringSessionFlowUtils';
import {
  buildMonitoringSessionTitle,
  selectMonitoringGeneratingCarouselSessions,
} from '../../../../../components/MonitoringAgent/utils/monitoringSessionUtils';
import {
  BOARD_INSTANCE_GROUP_TYPE,
  DEFAULT_SOCKET_FALLBACK_DELAY,
  SUPPLIER_DISCOVERY_SOCKET_FALLBACK_DELAY,
  VALUE_CHAIN_AI_AGENT,
  VALUE_CHAIN_MAP_ANALYSIS_DATE_FORMAT,
  VC_BOARD_TYPES,
} from '../../../../../constants/general';
import {
  VALUE_CHAIN_MAP_CUSTOM_NODES,
  VALUE_CHAIN_PANEL_NODES_TYPES,
  VALUE_CHAIN_UNIT_TYPES,
} from '../../../../../constants/interfaces';
import {
  isFinanceMonitoringSignalResult,
  isOwnershipMonitoringSignalResult,
} from '../../../../../constants/interfaces/IMonitoringSignalResult';
import {
  getMonitoringAgentByActiveKind,
  getMonitoringAgentBySessionType,
  REPUTATION_MONITORING_AGENT,
} from '../../../../../constants/monitoringAgents';
import useCreateNodesAndEdgesValueChain from '../../../../../hooks/useCreateNodesAndEdgesValueChain';
import {
  useActivateAgentSessionMutation,
  useCreateMonitoringScheduleMutation,
  useDeleteMonitoringScheduleMutation,
  useDeleteSessionMutation,
  useGetMonitoringSchedulesQuery,
  useGetOrgStructureValueChainMapQuery,
  useGetValueChainMapBoardFiltersQuery,
  useGetValueChainMapBoardQuery,
  useGetValueChainMapBoardStructureQuery,
  useGetValueChainMapBusinessModelQuery,
  useGetVCAgentSessionsQuery,
  usePauseMonitoringScheduleMutation,
  useResumeMonitoringScheduleMutation,
  useRunMonitoringScheduleNowMutation,
  useStartVCAgentSessionMutation,
  useUidNameTuplesVCQuery,
  useUpdateBoardUnitMutation,
  useUpdateMonitoringScheduleMutation,
} from '../../../../../store/slices/valueChainMap/apis/valueChainMapApi';
import {
  getUnitIdFromNodeId,
  normalizeAIGeneratingStatus,
} from '../../../../../utils/AICreatorHelper';
import { createCountryDetailsLookup, createCountryLookup } from '../../../../../utils/country';
import { extractApiErrorMessage, removeBackendErrorCodePrefix } from '../../../../../utils/helpers';
import { DEFAULT_FOLLOW_CONNECTIONS } from '../../../../../utils/valueChainUserFilters';
import { useDnD } from './components/DndContext';
import ValueChainLocationsProvider from './components/ValueChainLocationsProvider';
import ValueChainViewportController from './components/ValueChainViewportController';
import useDragAndDrop from './hooks/useDragAndDrop';
import useFilters from './hooks/useFilters';
import useUserFilters from './hooks/useUserFilters';
import useValueChainAgentSessionAiAction from './hooks/useValueChainAgentSessionAiAction';
import useValueChainAgentSessionState from './hooks/useValueChainAgentSessionState';
import useValueChainAgentSockets from './hooks/useValueChainAgentSockets';
import useValueChainAICards from './hooks/useValueChainAICards';
import useValueChainBoardGroups from './hooks/useValueChainBoardGroups';
import useValueChainBoardNodes from './hooks/useValueChainBoardNodes';
import useValueChainConnections from './hooks/useValueChainConnections';
import useValueChainDraftReview from './hooks/useValueChainDraftReview';
import useValueChainEdgeStyles from './hooks/useValueChainEdgeStyles';
import useValueChainGroupNodes from './hooks/useValueChainGroupNodes';
import useValueChainInitialLayout from './hooks/useValueChainInitialLayout';
import useValueChainInteractionState from './hooks/useValueChainInteractionState';
import useValueChainMonitoringSessionAiAction from './hooks/useValueChainMonitoringSessionAiAction';
import useValueChainNodeInteractions from './hooks/useValueChainNodeInteractions';
import useValueChainScenarioView from './hooks/useValueChainScenarioView';
import useValueChainSwimlanes from './hooks/useValueChainSwimlanes';
import {
  isMonitoringAgentKind,
} from './interfaces/TValueChainActiveAgentKind';
import applyUnitCollapseToNodes from './utils/applyUnitCollapseToNodes';
import {
  getBoardResolvableUnitIds,
  resolveMonitoringEntitiesFromBoard,
} from './utils/monitoringEntityResolution';
import applyMonitoringRiskStateToNodes from './utils/monitoringRiskBadgeUtils';
import { isDraftReviewReadyStatus } from './utils/valueChainAgentSessions';
import hasProfitCenterInBusinessModel from './utils/valueChainBusinessModel';
import {
  expandCountryGroupStackNodes,
} from './utils/valueChainNodeLayout';
import { getSectionForPanelType, isSectionValidForPanelType } from './utils/valueChainSectionValidation';
import ValueChainView from './ValueChainView';

import type { TNodeDragEvent } from 'utils/reactFlowHelpers';
import type { IAICardSelection } from '../../../../../components/AICardPanel/interfaces/IAICardSelection';
import type { IAIGeneratingModalSessionData } from '../../../../../components/AIGeneratingModal/interfaces/IAIGeneratingModal';
import type IScheduleConfigDraft from '../../../../../components/MonitoringAgent/interfaces/IScheduleConfigDraft';
import type { TMonitoringCardPanelFlow } from '../../../../../components/MonitoringAgent/interfaces/TMonitoringCardPanelFlow';
import type { TMonitoringResultsBreadcrumbMode } from '../../../../../components/MonitoringAgent/MonitoringResultsPanel/interfaces/IMonitoringResultsPanel';
import type {
  TValueChainMapModal,
} from '../../../../../constants/interfaces';
import type { TMonitoringHubTab } from '../../../../../constants/interfaces/IMonitoringHubPanelState';
import type IMonitoringSchedule from '../../../../../constants/interfaces/IMonitoringSchedule';
import type {
  IVCAgentSessionUnitUpdatedPayload,
  IVCAgentSessionUpdatedPayload,
} from '../../../../../hooks/interfaces/IUseWebSocket';
import type IOrgStructureItem from '../../../../../store/slices/dataSets/interfaces/IOrgStructureItem';
import type { TBoardInstanceGroupType } from '../../../../../store/slices/valueChainMap/interfaces/IBoardInstanceGroup';
import type { IGetValueChainBoardResponse } from '../../../../../store/slices/valueChainMap/interfaces/IGetValueChainBoardResponse';
import type ISessionVCItem from '../../../../../store/slices/valueChainMap/interfaces/ISessionVCItem';
import type { IValueChainBoardColumn } from '../../../../../store/slices/valueChainMap/interfaces/IValueChainBoardColumn';
import type { IValueChainBoardStructure } from '../../../../../store/slices/valueChainMap/interfaces/IValueChainBoardStructure';
import type { ICustomUnitNodeData } from './interfaces/ICustomUnitNode';
import type { IExistingSessionStatusOverride } from './interfaces/IUseValueChainDraftReview';
import type { TMonitoringSessionOpenRequest } from './interfaces/IUseValueChainMonitoringSessionAiAction';
import type { IValueChain } from './interfaces/IValueChain';
import type { TValueChainActiveAgentKind } from './interfaces/TValueChainActiveAgentKind';

import styles from './sass/ValueChainView.module.scss';

// Stable no-op keeps disabled handler props referentially equal between renders.
const noop = () => {};

function ValueChain({
  onMoveEnd, valueChainMapId, isFullScreenActive, isAccessDisabled,
  openProfitCenterModal, setSelectedProfitCenter, setCurrentTargetSwimlaneId,
  currentTargetSwimlaneId, isProfitCentreModalOpen, openCustomerModal, setSelectedCustomer,
  openSupplierModal, setSelectedSupplier, openBalanceSheetAssetsModal, setSelectedBalanceSheetAssets,
  openInputProductModal, setSelectedInputProduct, onRegisterReviewGeneratedBackHandler,
  onReviewGeneratedVisibilityChange, launchpadDiscoverEntry,
}: IValueChain) {
  const { t } = useTranslation();
  const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);
  const [activeModal, setActiveModal] = useState<TValueChainMapModal | undefined>(undefined);
  const [selectedBoard, setSelectedBoard] = useState<IValueChainBoardColumn | undefined>(undefined);
  const [openAIAgentsSidebar, setOpenAIAgentsSidebar] = useState<boolean>(false);
  const [openAISessionsSidebar, setOpenAISessionsSidebar] = useState<boolean>(false);
  const [openMonitoringHubSidebar, setOpenMonitoringHubSidebar] = useState<boolean>(false);
  const [openMonitoringResultsPanel, setOpenMonitoringResultsPanel] = useState<boolean>(false);
  const [monitoringResultsSessionTitle, setMonitoringResultsSessionTitle] = useState<string | undefined>(undefined);
  const [monitoringResultsSessionId, setMonitoringResultsSessionId] = useState<number | undefined>(undefined);
  const [monitoringResultsFallbackEntities, setMonitoringResultsFallbackEntities] = useState<
  { unitId: number, name: string }[]
  >([]);
  const [activeAgentKind, setActiveAgentKind] = useState<TValueChainActiveAgentKind>(null);
  const activeMonitoringAgent = useMemo(
    () => getMonitoringAgentByActiveKind(activeAgentKind),
    [activeAgentKind],
  );
  const [openAICardPanel, setOpenAICardPanel] = useState<boolean>(false);
  const [openScheduleConfigPanel, setOpenScheduleConfigPanel] = useState<boolean>(false);
  const [openScheduleRunHistoryPanel, setOpenScheduleRunHistoryPanel] = useState<boolean>(false);
  const [activeMonitoringSchedule, setActiveMonitoringSchedule] = useState<IMonitoringSchedule | undefined>(undefined);
  const [editingMonitoringSchedule, setEditingMonitoringSchedule] = useState<IMonitoringSchedule | undefined>(
    undefined,
  );
  const [monitoringCardPanelFlow, setMonitoringCardPanelFlow] = useState<TMonitoringCardPanelFlow>('manual-session');
  const [monitoringHubActiveTab, setMonitoringHubActiveTab] = useState<TMonitoringHubTab>('sessions');
  const [monitoringResultsRunId, setMonitoringResultsRunId] = useState<string | undefined>(undefined);
  // The execution the run history panel opens on, when the assistant started the session.
  const [monitoringResultsExecutionKey, setMonitoringResultsExecutionKey] = useState<string | undefined>(undefined);
  const [monitoringResultsBreadcrumbMode, setMonitoringResultsBreadcrumbMode] = useState<
  TMonitoringResultsBreadcrumbMode
  >('session');
  const [monitoringResultsScheduleTitle, setMonitoringResultsScheduleTitle] = useState<string | undefined>(undefined);
  const [monitoringResultsRunLabel, setMonitoringResultsRunLabel] = useState<string | undefined>(undefined);
  const [openAIGeneratingModal, setOpenAIGeneratingModal] = useState<boolean>(false);
  const [isMonitoringGeneratingOverview, setIsMonitoringGeneratingOverview] = useState<boolean>(false);
  const [openAICreatorBoMUnits, setOpenAICreatorBoMUnits] = useState<boolean>(false);
  const [bomUnitsCards, setBomUnitsCards] = useState<IAICardSelection[]>([]);
  const [bomUnitsSession, setBomUnitsSession] = useState<ISessionVCItem | undefined>(undefined);
  const [bomUnitsInitialTabNodeId, setBomUnitsInitialTabNodeId] = useState<string | undefined>(undefined);
  const [openAICreatorSupplierUnits, setOpenAICreatorSupplierUnits] = useState<boolean>(false);
  const [supplierUnitsCards, setSupplierUnitsCards] = useState<IAICardSelection[]>([]);
  const [supplierUnitsSession, setSupplierUnitsSession] = useState<ISessionVCItem | undefined>(undefined);
  const [supplierUnitsInitialTabNodeId, setSupplierUnitsInitialTabNodeId] = useState<string | undefined>(undefined);
  const [sessionId, setSessionId] = useState<number | undefined>(undefined);
  const [activeAgentSession, setActiveAgentSession] = useState<ISessionVCItem | undefined>(undefined);
  const [isNewNTierSelection, setIsNewNTierSelection] = useState(false);
  const [isNewMonitoringSelection, setIsNewMonitoringSelection] = useState(false);
  const [nTierLaneSubType, setNTierLaneSubType] = useState<string | null>(null);
  const [sessionIdOpeningInEditMode, setSessionIdOpeningInEditMode] = useState<number | undefined>(undefined);
  const [isAISessionOpeningInEditMode, setIsAISessionOpeningInEditMode] = useState(false);
  const [draftReviewSession, setDraftReviewSession] = useState<ISessionVCItem | undefined>(undefined);
  const [isDraftReviewModeEnabled, setIsDraftReviewModeEnabled] = useState(false);
  const [pendingAppliedBomUnitIds, setPendingAppliedBomUnitIds] = useState<number[]>([]);
  const [monitoringAgentConfigId, setMonitoringAgentConfigId] = useState<number | null>(null);
  const [isAIGeneratingFloatingButtonEnabled, setIsAIGeneratingFloatingButtonEnabled] = useState(false);
  const [isBomDraftFloatingButtonVisible, setIsBomDraftFloatingButtonVisible] = useState(false);
  const [isAISocketLaunchRequested, setIsAISocketLaunchRequested] = useState(false);
  const [isSocketPollingFallbackActive, setIsSocketPollingFallbackActive] = useState(false);
  const isSupplierDiscoveryGenerationRequestedRef = useRef(false);
  const launchpadDiscoverHandledRef = useRef(false);
  const [existingSessionStatusOverride, setExistingSessionStatusOverride] = useState<
  IExistingSessionStatusOverride | undefined
  >(undefined);
  const [ignoredExistingSessionId, setIgnoredExistingSessionId] = useState<number | undefined>(undefined);
  const [hasUnreadAIGeneratingResults, setHasUnreadAIGeneratingResults] = useState(false);
  const [isSupplierGeneratingPending, setIsSupplierGeneratingPending] = useState(false);

  const [panelType, setPanelType] = useDnD();
  const {
    screenToFlowPosition,
    fitBounds,
    getNodesBounds,
    getZoom,
  } = useReactFlow();

  const { onNodeDoubleClick } = useValueChainNodeInteractions({
    openProfitCenterModal,
    setSelectedProfitCenter,
    setCurrentTargetSwimlaneId,
    openCustomerModal,
    setSelectedCustomer,
    openSupplierModal,
    setSelectedSupplier,
    openBalanceSheetAssetsModal,
    setSelectedBalanceSheetAssets,
    openInputProductModal,
    setSelectedInputProduct,
  });

  const lastHandledBomSocketPayloadKeyRef = useRef<string | undefined>(undefined);
  const hadBackgroundProcessingRef = useRef(false);
  const isNodeDraggingRef = useRef(false);
  const socketFallbackDelayRef = useRef(DEFAULT_SOCKET_FALLBACK_DELAY);
  const prevNodesSnapshotRef = useRef<Node[]>([]);
  const nodesRef = useRef<Node[]>([]);
  const creatingTargetIdRef = useRef<number | undefined>(undefined);

  const [lastBomSocketPayload, setLastBomSocketPayload] = useState<IVCAgentSessionUnitUpdatedPayload | null>(null);

  const {
    data: locationsCountries,
    isFetching: isLocationsCountriesFetching,
    isSuccess: isLocationsCountriesSuccess,
    isError: isLocationsCountriesError,
  } = useGetLocationsCountriesQuery();
  const countryLookup = useMemo(
    () => createCountryLookup(locationsCountries?.data),
    [locationsCountries?.data],
  );

  const [requestFiltersState, setRequestFiltersState] = useState<string | undefined>(undefined);
  const [followConnections, setFollowConnections] = useState<boolean>(DEFAULT_FOLLOW_CONNECTIONS);
  // Board requests hold until saved filters are known, so opening a map issues one
  // request instead of loading unfiltered and immediately reloading filtered.
  // Released per map id rather than as a bare flag: a new map closes the gate
  // during render, before the board query can fire with the old map's filters.
  // Set by useUserFilters, which is called below, once selectedCurrentFilters exists.
  const [restoreReleasedMapId, setRestoreReleasedMapId] = useState<string | undefined>(undefined);
  const isRestorePending = valueChainMapId !== restoreReleasedMapId;
  const parsedValueChainMapId = useMemo(() => {
    const parsed = Number(valueChainMapId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
  }, [valueChainMapId]);
  const {
    data: monitoringSchedulesResponse,
    isFetching: isMonitoringSchedulesFetching,
    isError: isMonitoringSchedulesLoadFailed,
  } = useGetMonitoringSchedulesQuery({
    valueChainMapId: parsedValueChainMapId || 0,
    sessionType: activeMonitoringAgent.sessionType,
  }, {
    skip: !parsedValueChainMapId,
  });
  const monitoringSchedules = monitoringSchedulesResponse?.data ?? [];
  const [groupByType, setGroupByType] = useState<TBoardInstanceGroupType>(BOARD_INSTANCE_GROUP_TYPE.GENERIC);
  const isGenericGroupBy = groupByType === BOARD_INSTANCE_GROUP_TYPE.GENERIC;
  const isGroupingMode = !isGenericGroupBy;
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<number>>(() => new Set<number>());
  const [showAllGroupIds, setShowAllGroupIds] = useState<Set<number>>(() => new Set<number>());
  const [isGroupSwitchRendering, setIsGroupSwitchRendering] = useState<boolean>(false);
  const groupSwitchFrameRef = useRef<number>(0);
  const handleToggleGroup = useCallback((groupId: number) => {
    setExpandedGroupIds((prevGroupIds) => {
      const nextGroupIds = new Set(prevGroupIds);
      if (nextGroupIds.has(groupId)) {
        nextGroupIds.delete(groupId);
      } else {
        nextGroupIds.add(groupId);
      }
      return nextGroupIds;
    });
    setShowAllGroupIds((prevGroupIds) => {
      if (!prevGroupIds.has(groupId)) {
        return prevGroupIds;
      }
      const nextGroupIds = new Set(prevGroupIds);
      nextGroupIds.delete(groupId);
      return nextGroupIds;
    });
  }, []);
  const handleShowMoreGroupUnits = useCallback((groupId: number) => {
    setShowAllGroupIds((prevGroupIds) => {
      const nextGroupIds = new Set(prevGroupIds);
      nextGroupIds.add(groupId);
      return nextGroupIds;
    });
  }, []);
  useEffect(() => {
    setExpandedGroupIds(new Set<number>());
    setShowAllGroupIds(new Set<number>());
  }, [groupByType, valueChainMapId]);
  const onToggleFollowConnections = useCallback((checked: boolean) => {
    setFollowConnections(checked);
  }, []);
  const {
    data: vcaAgentSessions,
    isSuccess: isVCAgentSessionsLoaded,
    isError: isVCAgentSessionsLoadFailed,
    isFetching: isVCAgentSessionsFetching,
    refetch: refetchVCAgentSessions,
    fulfilledTimeStamp: agentSessionsFulfilledTimeStamp,
  } = useGetVCAgentSessionsQuery({ valueChainMapId: valueChainMapId || '' }, {
    skip: !valueChainMapId,
  });
  const [deleteSession, { isLoading: isDeleteSessionLoading }] = useDeleteSessionMutation();
  const [createMonitoringSchedule, { isLoading: isCreateMonitoringScheduleLoading }] = (
    useCreateMonitoringScheduleMutation()
  );
  const [deleteMonitoringSchedule, { isLoading: isDeleteMonitoringScheduleLoading }] = (
    useDeleteMonitoringScheduleMutation()
  );
  const [pauseMonitoringSchedule, { isLoading: isPauseMonitoringScheduleLoading }] = (
    usePauseMonitoringScheduleMutation()
  );
  const [resumeMonitoringSchedule, { isLoading: isResumeMonitoringScheduleLoading }] = (
    useResumeMonitoringScheduleMutation()
  );
  const [updateMonitoringSchedule, { isLoading: isUpdateMonitoringScheduleLoading }] = (
    useUpdateMonitoringScheduleMutation()
  );
  const [runMonitoringScheduleNow, { isLoading: isRunMonitoringScheduleNowLoading }] = (
    useRunMonitoringScheduleNowMutation()
  );
  const [activateAgentSession, { isLoading: isActivateAgentSessionLoading }] = useActivateAgentSessionMutation();
  const [startVCAgentSession] = useStartVCAgentSessionMutation();

  const {
    // `data`, not `currentData`: the previous board stays rendered while a new
    // filter set is in flight, instead of blanking for the request duration.
    data: genericBoardData,
    isLoading: isGenericBoardLoading,
    isFetching: isGenericBoardFetching,
    isError: isGenericBoardError,
    error: genericBoardError,
    refetch: refetchValueChainMapBoard,
  } = useGetValueChainMapBoardQuery({
    valueChainMapId: valueChainMapId || '',
    filters: requestFiltersState,
    followConnections,
  }, {
    skip: !valueChainMapId || !isGenericGroupBy || isRestorePending,
    refetchOnMountOrArgChange: true,
  });
  const {
    data: boardStructureData,
    isLoading: isBoardStructureLoading,
    isFetching: isBoardStructureFetching,
  } = useGetValueChainMapBoardStructureQuery({
    valueChainMapId: valueChainMapId || '',
  }, {
    skip: !valueChainMapId || isGenericGroupBy || isRestorePending,
  });

  // Fetched in both modes: this endpoint takes no filters, so its catalogue never
  // shrinks under applied filters and is safe to rehydrate saved filters against.
  const {
    data: boardFiltersData,
    isSuccess: isBoardFiltersSuccess,
    isError: isBoardFiltersError,
  } = useGetValueChainMapBoardFiltersQuery({
    valueChainMapId: valueChainMapId || '',
  }, {
    skip: !valueChainMapId,
  });

  const boardStructureResponse = useMemo<IGetValueChainBoardResponse | undefined>(() => {
    const boardStructureList = Array.isArray(boardStructureData)
      ? boardStructureData
      : (boardStructureData as { data?: IValueChainBoardStructure[] } | undefined)?.data;

    if (isGenericGroupBy || !boardStructureList) {
      return undefined;
    }

    return {
      data: {
        boards: boardStructureList.map((board) => ({
          id: board.id,
          name: board.name,
          valueChainMapId: board.valueChainMapId,
          columns: board.columns.map((column) => ({
            id: column.id,
            name: column.name,
            orderIndex: column.orderIndex,
            type: column.type,
            units: [],
          })),
        })),
        filters: {
          countryCode: [],
          revenue: { name: '' },
          uniqueIdentifierFilters: {},
        },
      },
    };
  }, [boardStructureData, isGenericGroupBy]);

  // Retaining `data` keeps the previous board on screen while a new filter set is
  // in flight. A failed request is different: there is no result coming, so the
  // old board would sit there contradicting the filter chips. Drop it instead.
  const genericBoardDataForCurrentRequest = isGenericBoardError ? undefined : genericBoardData;
  const data = isGenericGroupBy ? genericBoardDataForCurrentRequest : boardStructureResponse;
  const isLoading = isGenericGroupBy ? isGenericBoardLoading : isBoardStructureLoading;
  const isBoardFetching = isGenericGroupBy ? isGenericBoardFetching : isBoardStructureFetching;

  // Structural change: retained data belongs to the previous map/grouping and must
  // not linger. Filter changes deliberately do NOT clear here — the board keeps
  // rendering the previous result until the new one arrives.
  useEffect(() => {
    prevNodesSnapshotRef.current = [];
    nodesRef.current = [];
    setNodes([]);
    setEdges([]);
  }, [valueChainMapId, groupByType, setEdges, setNodes]);

  useEffect(() => {
    if (!isGenericGroupBy || genericBoardDataForCurrentRequest) {
      return;
    }

    prevNodesSnapshotRef.current = [];
    nodesRef.current = [];
    setNodes([]);
    setEdges([]);
  }, [
    genericBoardDataForCurrentRequest,
    isGenericGroupBy,
    setEdges,
    setNodes,
  ]);

  // The board clears itself on a failed request, so say why. Without this the
  // map just empties and the loader stops, with nothing to act on.
  useEffect(() => {
    if (!isGenericGroupBy || !isGenericBoardError) {
      return;
    }

    toast.error(extractApiErrorMessage(genericBoardError) || t('valueChainMap.boardLoadError'));
  }, [isGenericBoardError, genericBoardError, isGenericGroupBy, t]);

  const boardStructureList = useMemo<IValueChainBoardStructure[]>(() => {
    if (isGenericGroupBy) {
      return [];
    }

    return Array.isArray(boardStructureData)
      ? boardStructureData
      : (boardStructureData as { data?: IValueChainBoardStructure[] } | undefined)?.data || [];
  }, [boardStructureData, isGenericGroupBy]);

  const {
    data: profitCenterTuples,
    isLoading: isProfitCenterTuplesLoading,
  } = useUidNameTuplesVCQuery({
    types: [UID_NAME_TUPLES.profitCenter],
    valueChainMapId: valueChainMapId || '',
  }, {
    skip: !valueChainMapId || groupByType !== BOARD_INSTANCE_GROUP_TYPE.ORGANIZATIONAL,
  });

  // Map profit-center UID -> display name. The raw tuple option carries the UID
  // in `value` and the name in `label`/`name` (selectUidNameTuplesVC overwrites
  // the label with the value, so it cannot be used to resolve names here).
  const profitCenterNameById = useMemo<Map<string, string>>(() => {
    const nameById = new Map<string, string>();
    const profitCenterTuple = profitCenterTuples?.data?.find(
      (tuple) => tuple.name === UID_NAME_TUPLES.profitCenter,
    );

    profitCenterTuple?.enum?.forEach((option) => {
      nameById.set(option.value, option.label || option.name || option.value);
    });

    return nameById;
  }, [profitCenterTuples]);

  const {
    groupsByBoardId,
    unitsByBoardId,
    isLoading: isBoardGroupsLoading,
  } = useValueChainBoardGroups({
    valueChainMapId,
    boards: boardStructureList,
    type: groupByType,
    enabled: isGroupingMode && Boolean(valueChainMapId),
    filters: requestFiltersState,
    followConnections,
  });

  useValueChainGroupNodes({
    isActive: isGroupingMode,
    boards: boardStructureList,
    groupsByBoardId,
    unitsByBoardId,
    expandedGroupIds,
    showAllGroupIds,
    groupByType,
    countryLookup,
    profitCenterNameById,
    onToggleGroup: handleToggleGroup,
    onShowMoreGroupUnits: handleShowMoreGroupUnits,
    setNodes,
    setEdges,
  });

  const {
    data: businessModelData,
    isFetching: isBusinessModelFetching,
  } = useGetValueChainMapBusinessModelQuery({ valueChainMapId: valueChainMapId || '' });
  const hasProfitCenter = useMemo(
    () => hasProfitCenterInBusinessModel(businessModelData?.data),
    [businessModelData?.data],
  );

  const [updateBoardUnit] = useUpdateBoardUnitMutation();

  const {
    rawExistingSession,
    existingSession,
    aiSessionPreviewSelectedUnitIds,
    liveBomUnitsSession,
    liveSupplierUnitsSession,
    liveDraftReviewSession,
    isLiveDraftReviewSessionReady,
    hasSupplierNTierAttention,
    hasReputationMonitoringAttention,
    hasRegulationMonitoringAttention,
    hasFinanceMonitoringAttention,
    hasOwnershipMonitoringAttention,
    hasClimateMonitoringAttention,
    existingMonitoringSession,
    effectiveMonitoringSession,
    isMonitoringSessionGenerating,
    hasProcessingAgentSessions,
    hasAIGeneratingSessions,
    hasActiveAgentSessionProcessing,
    hasPollingProcessingStatuses,
  } = useValueChainAgentSessionState({
    agentSessions: vcaAgentSessions?.data,
    sessionId,
    activeAgentSession,
    ignoredExistingSessionId,
    existingSessionStatusOverride,
    monitoringSessionType: isMonitoringAgentKind(activeAgentKind)
      ? activeMonitoringAgent.sessionType
      : undefined,
    openAISessionsSidebar,
    bomUnitsSession,
    supplierUnitsSession,
    draftReviewSession,
    isAIGeneratingFloatingButtonEnabled,
    hasUnreadAIGeneratingResults,
    isSupplierGeneratingPending,
    lastBomSocketPayload,
  });

  const monitoringMockTimerRef = useRef<number | null>(null);
  const pendingSessionReviewOpenRef = useRef<number | undefined>(undefined);

  const hideAIGeneratingFloatingButton = useCallback(() => {
    setIsAIGeneratingFloatingButtonEnabled(false);
    setIsSupplierGeneratingPending(false);
    setIsBomDraftFloatingButtonVisible(false);
    setHasUnreadAIGeneratingResults(false);
  }, []);

  const onSupplierResultsDone = useCallback(() => {
    setIsSupplierGeneratingPending(false);
    setHasUnreadAIGeneratingResults(true);
  }, []);

  const onSessionReviewPending = useCallback((nextSessionId: number) => {
    pendingSessionReviewOpenRef.current = nextSessionId;
  }, []);

  // The backend undid a monitoring start: none of its runs could be sent. The results the panel
  // shows for that session are gone, so the panel says why, as other agent errors are shown, and
  // goes back to the Sessions list, where the session can be started again.
  const onMonitoringSessionStartUndone = useCallback((payload: IVCAgentSessionUpdatedPayload) => {
    const { sessionId: undoneSessionId, message } = payload;

    if (!openScheduleRunHistoryPanel || activeMonitoringSchedule || monitoringResultsSessionId !== undoneSessionId) {
      return;
    }

    if (message?.trim()) {
      toast.error(removeBackendErrorCodePrefix(message));
    }

    setOpenScheduleRunHistoryPanel(false);
    setMonitoringResultsSessionId(undefined);
    setMonitoringResultsSessionTitle(undefined);
    setMonitoringResultsExecutionKey(undefined);
    setMonitoringHubActiveTab('sessions');
    setOpenMonitoringHubSidebar(true);
  }, [activeMonitoringSchedule, monitoringResultsSessionId, openScheduleRunHistoryPanel]);

  const {
    isBomSocketConnected,
    sendBomSocketUnitUpdated,
    clearSocketNoMessageTimer,
    startSocketPollingFallbackTimer,
  } = useValueChainAgentSockets({
    valueChainMapId,
    isLaunchRequested: isAISocketLaunchRequested,
    sessionFilter: {
      existingId: existingSession?.id,
      bomUnitsId: liveBomUnitsSession?.id,
      supplierUnitsId: liveSupplierUnitsSession?.id,
      draftReviewId: draftReviewSession?.id,
      monitoringId: isMonitoringAgentKind(activeAgentKind)
        ? effectiveMonitoringSession?.id
        : undefined,
      monitoringResultsId: openScheduleRunHistoryPanel && !activeMonitoringSchedule
        ? monitoringResultsSessionId
        : undefined,
    },
    socketFallbackDelayRef,
    isAIGeneratingFloatingButtonEnabled,
    refetchVCAgentSessions,
    setLastBomSocketPayload,
    setIsSocketPollingFallbackActive,
    onSupplierResultsDone,
    onSessionReviewPending,
    onMonitoringSessionStartUndone,
  });

  useEffect(() => {
    if (sessionId) {
      setIsNewNTierSelection(false);
      setIsNewMonitoringSelection(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!existingSessionStatusOverride) {
      return;
    }

    if (!rawExistingSession || rawExistingSession.id !== existingSessionStatusOverride.sessionId) {
      setExistingSessionStatusOverride(undefined);
      return;
    }

    if (rawExistingSession.status === existingSessionStatusOverride.status) {
      setExistingSessionStatusOverride(undefined);
    }
  }, [existingSessionStatusOverride, rawExistingSession]);

  useEffect(() => {
    if (!ignoredExistingSessionId) {
      return;
    }

    if (!rawExistingSession || rawExistingSession.id !== ignoredExistingSessionId) {
      setIgnoredExistingSessionId(undefined);
    }
  }, [ignoredExistingSessionId, rawExistingSession]);

  const handleToggleUnitCollapse = useCallback((nodeId: string) => {
    setNodes((prevNodes) => applyUnitCollapseToNodes(prevNodes, nodeId));
  }, [setNodes]);

  const handleExpandCountryGroupStack = useCallback((countryGroupId: string) => {
    const { nodeIdsToReveal } = expandCountryGroupStackNodes(nodesRef.current, countryGroupId);

    if (nodeIdsToReveal.length === 0) {
      return;
    }

    setNodes((prevNodes) => expandCountryGroupStackNodes(prevNodes, countryGroupId).nodes);

    if (openAICardPanel) {
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const nodesToFocus = nodesRef.current.filter((node) => nodeIdsToReveal.includes(node.id));

        if (!nodesToFocus.length) {
          return;
        }

        fitBounds(getNodesBounds(nodesToFocus), {
          padding: 0.2,
          duration: 300,
        });
      });
    });
  }, [fitBounds, getNodesBounds, openAICardPanel, setNodes]);

  const aiPanelSelectableUnitTypes = useMemo(() => {
    const baseTypes = resolveAIPanelSelectableUnitTypes(activeAgentKind);

    if (nTierLaneSubType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE) {
      return new Set([VALUE_CHAIN_UNIT_TYPES.PRODUCT]);
    }

    return baseTypes;
  }, [activeAgentKind, nTierLaneSubType]);
  const isMonitoringCrossSwimlaneSelection = isMonitoringAgentKind(activeAgentKind);
  const monitoringSelectionMode = isMonitoringAgentKind(activeAgentKind)
    ? activeMonitoringAgent.selectionMode
    : undefined;
  const isUnitSelectionUnlocked = monitoringCardPanelFlow === 'new-schedule' || Boolean(sessionId);

  const {
    selectedAICards,
    setSelectedAICards,
    generatingAICards,
    setGeneratingAICards,
    allAvailableAICards,
    availableBoMManufacturerCards,
    generatingAISessions,
    getSelectedAICardsFromUnitIds,
    getSelectedCardsFromSession,
    onToggleAIPanelCardSelection,
    onSyncSelectedAICardsFromSession,
    onRemoveSelectedAICard,
    onResetSelectedAICards,
    applyAIPanelStateToNodes,
  } = useValueChainAICards({
    nodes,
    nodesRef,
    openAICardPanel,
    isUnitSelectionUnlocked,
    aiSessionPreviewSelectedUnitIds,
    agentSessions: vcaAgentSessions?.data,
    bomUnitsCards,
    supplierUnitsCards,
    selectableUnitTypes: aiPanelSelectableUnitTypes,
    allowCrossSwimlaneSelection: isMonitoringCrossSwimlaneSelection,
    monitoringSelectionMode,
    onExpandCountryGroupStack: handleExpandCountryGroupStack,
    onToggleUnitCollapse: handleToggleUnitCollapse,
  });

  const generatingMonitoringSessions = useMemo<IAIGeneratingModalSessionData[]>(() => {
    const sessionsWithStatusOverride = (vcaAgentSessions?.data || []).map((sessionItem) => (
      existingSessionStatusOverride?.sessionId === sessionItem.id
        ? { ...sessionItem, status: existingSessionStatusOverride.status }
        : sessionItem
    ));

    return selectMonitoringGeneratingCarouselSessions(sessionsWithStatusOverride).map((sessionItem) => ({
      session: sessionItem,
      selectedCards: getSelectedCardsFromSession(sessionItem),
    }));
  }, [
    existingSessionStatusOverride,
    getSelectedCardsFromSession,
    vcaAgentSessions?.data,
  ]);

  const closeAIFlow = useCallback(() => {
    setOpenAIAgentsSidebar(false);
    setOpenAISessionsSidebar(false);
    setOpenMonitoringHubSidebar(false);
    setOpenAICardPanel(false);
    setOpenScheduleConfigPanel(false);
    setOpenScheduleRunHistoryPanel(false);
    setOpenMonitoringResultsPanel(false);
    setActiveMonitoringSchedule(undefined);
    setEditingMonitoringSchedule(undefined);
    setMonitoringCardPanelFlow('manual-session');
    setMonitoringResultsRunId(undefined);
    setMonitoringResultsBreadcrumbMode('session');
    setMonitoringResultsScheduleTitle(undefined);
    setMonitoringResultsRunLabel(undefined);
    setMonitoringResultsSessionTitle(undefined);
    setMonitoringResultsSessionId(undefined);
    setMonitoringResultsFallbackEntities([]);
    setSelectedAICards([]);
    setMonitoringAgentConfigId(null);
    setActiveAgentKind(null);
    setIsMonitoringGeneratingOverview(false);
    setNTierLaneSubType(null);
  }, [setSelectedAICards]);

  useEffect(() => {
    if (!openAICreatorBoMUnits) {
      return;
    }

    setOpenAICardPanel(false);
    setOpenAIGeneratingModal(false);
    setOpenAICreatorSupplierUnits(false);
    setGeneratingAICards([]);
  }, [openAICreatorBoMUnits, setGeneratingAICards]);

  useEffect(() => {
    if (!openAICreatorSupplierUnits) {
      return;
    }

    setOpenAICardPanel(false);
    setOpenAIGeneratingModal(false);
    setOpenAICreatorBoMUnits(false);
    setGeneratingAICards([]);
  }, [openAICreatorSupplierUnits, setGeneratingAICards]);

  const {
    boardData,
    activeDraftSession,
    draftCardToDeleteName,
    isDraftReviewMode,
    isAIDraftUnitsReadyForSelectionSync,
    isUseDraftResultDisabled,
    isDraftDeleteModalOpen,
    isDraftUnitsLoading,
    isDeleteDraftSessionUnitsLoading,
    isApplyDraftSessionLoading,
    isDeleteDraftSessionLoading,
    isTransitioningToDraftReview,
    isBoardRefetchingAfterApply,
    isDraftBoardBuilding,
    setDraftData,
    setCachedDraftData,
    setCachedDraftSessionId,
    setIsTransitioningToDraftReview,
    refetchDraftUnits,
    onDeleteDraftUnit,
    onRejectDraftDelete,
    onConfirmDraftDelete,
    onDiscardDraftResult,
    onUseDraftResult,
  } = useValueChainDraftReview({
    valueChainMapId,
    boardDataResponse: data,
    isBoardFetching,
    nodesLength: nodes.length,
    nodesRef,
    setNodes,
    setEdges,
    existingSession,
    liveDraftReviewSession,
    isLiveDraftReviewSessionReady,
    draftReviewSession,
    setDraftReviewSession,
    isDraftReviewModeEnabled,
    setIsDraftReviewModeEnabled,
    getSelectedCardsFromSession,
    closeAIFlow,
    refetchValueChainMapBoard,
    onRegisterReviewGeneratedBackHandler,
    onReviewGeneratedVisibilityChange,
    setSelectedAICards,
    setGeneratingAICards,
    setBomUnitsCards,
    setBomUnitsSession,
    setSupplierUnitsCards,
    setSupplierUnitsSession,
    setSupplierUnitsInitialTabNodeId,
    setSessionId,
    setActiveAgentSession,
    setOpenAIGeneratingModal,
    setOpenAICreatorBoMUnits,
    setOpenAICreatorSupplierUnits,
    setIsAIGeneratingFloatingButtonEnabled,
    setIsBomDraftFloatingButtonVisible,
    setHasUnreadAIGeneratingResults,
    setPendingAppliedBomUnitIds,
    setExistingSessionStatusOverride,
    setIgnoredExistingSessionId,
  });

  const { results: monitoringResultsForBadges } = useMonitoringResults({
    valueChainMapId: parsedValueChainMapId,
    sessionId: monitoringResultsSessionId,
    runId: monitoringResultsRunId,
    isEnabled: openMonitoringResultsPanel,
    resultsMode: activeMonitoringAgent.resultsMode,
    fallbackEntities: monitoringResultsFallbackEntities,
  });

  const monitoringAtRiskUnitIds = useMemo(
    () => {
      if (!openMonitoringResultsPanel) {
        return new Set<number>();
      }

      return getAtRiskUnitIds(
        monitoringResultsForBadges,
        monitoringResultsFallbackEntities.map((entity) => entity.unitId),
        monitoringResultsFallbackEntities,
      );
    },
    [
      monitoringResultsFallbackEntities,
      monitoringResultsForBadges,
      openMonitoringResultsPanel,
    ],
  );

  const monitoringImpactBridgeUnitIds = useMemo(() => {
    const boardUnitIds = getBoardResolvableUnitIds(nodes);
    const resolvedEntities = resolveMonitoringEntitiesFromBoard(
      nodes,
      monitoringResultsFallbackEntities,
    );
    const monitoredOnBoard = resolvedEntities
      .map((entity) => entity.unitId)
      .filter((unitId) => boardUnitIds.has(unitId));

    return Array.from(getAtRiskUnitIds(
      monitoringResultsForBadges,
      monitoredOnBoard,
      resolvedEntities,
    ));
  }, [monitoringResultsForBadges, monitoringResultsFallbackEntities, nodes]);

  const showMonitoringImpactScenarioCta = useMemo(
    () => shouldShowMonitoringImpactScenarioCta(
      monitoringResultsForBadges,
      monitoringImpactBridgeUnitIds,
    ),
    [monitoringImpactBridgeUnitIds, monitoringResultsForBadges],
  );

  const applyMonitoringRiskBadgeStateToNodes = useCallback(
    (nodesToDecorate: Node[]) => applyMonitoringRiskStateToNodes(
      nodesToDecorate,
      monitoringAtRiskUnitIds,
      isDraftReviewMode,
      activeMonitoringAgent.selectionMode,
    ),
    [activeMonitoringAgent.selectionMode, isDraftReviewMode, monitoringAtRiskUnitIds],
  );

  const applyCanvasUnitStateToNodes = useCallback(
    (nodesToDecorate: Node[]) => applyMonitoringRiskBadgeStateToNodes(
      applyAIPanelStateToNodes(nodesToDecorate),
    ),
    [applyAIPanelStateToNodes, applyMonitoringRiskBadgeStateToNodes],
  );

  const openSessionReviewResults = useCallback((sessionItem?: ISessionVCItem) => {
    if (!sessionItem) {
      return;
    }

    pendingSessionReviewOpenRef.current = undefined;
    setIsAIGeneratingFloatingButtonEnabled(false);
    setIsSupplierGeneratingPending(false);
    setIsBomDraftFloatingButtonVisible(false);
    setHasUnreadAIGeneratingResults(false);
    setOpenAIAgentsSidebar(false);
    setOpenAICardPanel(false);
    setOpenAIGeneratingModal(false);
    setOpenAICreatorBoMUnits(false);
    setOpenAICreatorSupplierUnits(false);
    setBomUnitsCards([]);
    setBomUnitsSession(undefined);
    setBomUnitsInitialTabNodeId(undefined);
    setSupplierUnitsCards([]);
    setSupplierUnitsSession(undefined);
    setSupplierUnitsInitialTabNodeId(undefined);
    setGeneratingAICards([]);
    setDraftData(undefined);
    setCachedDraftData(undefined);
    setCachedDraftSessionId(undefined);
    setDraftReviewSession(sessionItem);
    setIsDraftReviewModeEnabled(true);
    setSelectedAICards(getSelectedCardsFromSession(sessionItem));
  }, [
    getSelectedCardsFromSession,
    setCachedDraftData,
    setCachedDraftSessionId,
    setDraftData,
    setGeneratingAICards,
    setSelectedAICards,
  ]);

  useEffect(() => {
    const pendingSessionId = pendingSessionReviewOpenRef.current;

    if (
      !openAIGeneratingModal
      || !pendingSessionId
      || rawExistingSession?.id !== pendingSessionId
    ) {
      return;
    }

    const isReviewStatus = rawExistingSession.status === VALUE_CHAIN_AI_AGENT.STATUS_REVIEW
      || rawExistingSession.status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT;

    if (!isReviewStatus) {
      return;
    }

    pendingSessionReviewOpenRef.current = undefined;
    openSessionReviewResults(rawExistingSession);
  }, [openAIGeneratingModal, openSessionReviewResults, rawExistingSession]);

  const {
    isInitialViewportReady,
    viewportSizeRef,
    viewportYRef,
    viewportZoomRef,
    groupedDataRef,
    hasCompletedInitialLayoutRef,
    handleViewportSizeReady,
    handleViewportSync,
    handleViewportSizeSync,
    handleGroupedDataSync,
  } = useValueChainInitialLayout({
    nodes,
    isLoading,
    boardData,
    setNodes,
    nodesRef,
  });

  const groupingFiltersSource = useMemo(
    () => (boardFiltersData ? { data: { filters: boardFiltersData.data } } : undefined),
    [boardFiltersData],
  );

  const {
    data: orgStructureData,
    isSuccess: isOrgStructureSuccess,
    isError: isOrgStructureError,
  } = useGetOrgStructureValueChainMapQuery({
    valueChainMapId: valueChainMapId || '',
  }, {
    skip: !valueChainMapId,
  });

  const {
    openFilters,
    filtersData,
    selectedCurrentFilters,
    setSelectedCurrentFilters,
    requestFilters,
    onOpenFilters,
    setOpenFilters,
    onApplyFilters,
  } = useFilters({
    data: isGroupingMode ? groupingFiltersSource : boardData,
    locationsCountries,
  });

  const orgStructureLeaves = useMemo<IOrgStructureItem[]>(() => {
    const collectLeaves = (items: IOrgStructureItem[]): IOrgStructureItem[] => items
      .filter((item) => !item.archived)
      .flatMap((item) => {
        const childLeaves = collectLeaves(item.children);
        return childLeaves.length > 0 ? childLeaves : [item];
      });

    return collectLeaves(orgStructureData?.data || []);
  }, [orgStructureData]);

  const countryDetailsLookup = useMemo(
    () => createCountryDetailsLookup(locationsCountries?.data),
    [locationsCountries?.data],
  );

  // Settled, not loaded: a failed supporting query still releases the board.
  // Filters that cannot be resolved are dropped rather than blocking the map.
  const areUserFilterDependenciesSettled = (isBoardFiltersSuccess || isBoardFiltersError)
    && (isLocationsCountriesSuccess || isLocationsCountriesError)
    && (isOrgStructureSuccess || isOrgStructureError);

  const { isRestorePending: isUserFiltersRestorePending } = useUserFilters({
    valueChainMapId,
    rawCatalogue: boardFiltersData?.data,
    countryDetailsLookup,
    orgStructureLeaves,
    selectedCurrentFilters,
    followConnections,
    groupByType,
    areDependenciesSettled: areUserFilterDependenciesSettled,
    setSelectedCurrentFilters,
    setFollowConnections,
    setGroupByType,
    applyFilters: onApplyFilters,
  });

  useEffect(() => {
    if (!isUserFiltersRestorePending) {
      setRestoreReleasedMapId(valueChainMapId);
    }
  }, [isUserFiltersRestorePending, valueChainMapId]);

  useEffect(() => {
    setRequestFiltersState(requestFilters);
  }, [requestFilters]);

  const {
    boardEntities,
    isScenarioViewMode,
    openImpactScenariosSidebar,
    selectedImpactAnalysis,
    impactScenarios,
    isImpactScenariosLoading,
    isImpactScenarioCreating,
    isImpactScenariosDeleting,
    isImpactScenarioUpdating,
    isCreateScenarioSaving,
    valueChainMapAnalysisById,
    isAnalysisDetailsLoading,
    valueChainMapAnalysisResults,
    isAnalysisResultsLoading,
    hasMoreAnalysisResults,
    valueChainMapAnalysisScenarios,
    isAnalysisScenariosLoading,
    hasMoreAnalysisScenarios,
    isCopyAnalysisResultLoading,
    isCopyAnalysisScenariosLoading,
    isDeleteAnalysisResultLoading,
    isStartImpactAnalysisLoading,
    selectedScenarioId,
    activeScenarioId,
    activeResultId,
    followedResultId,
    openRequestId,
    openResultId,
    onOpenFilters: handleOpenFilters,
    onResetImpactScenariosView,
    onOpenImpactScenariosSidebar,
    onCloseImpactScenariosSidebar,
    onBackToImpactScenariosSidebar: handleBackToImpactScenarios,
    onCreateImpactScenario: handleCreateImpactScenario,
    onOpenImpactAnalysis: handleOpenImpactAnalysis,
    onRenameImpactAnalysis: handleRenameImpactAnalysis,
    onDeleteImpactScenario,
    onSaveScenario: handleSaveScenario,
    onUpdateScenario: handleUpdateAnalysisScenario,
    onDeleteAnalysisScenario,
    onLoadAnalysisResults: handleLoadAnalysisResults,
    onLoadMoreAnalysisResults: handleLoadMoreAnalysisResults,
    onLoadAnalysisScenarios: handleLoadAnalysisScenarios,
    onLoadMoreAnalysisScenarios: handleLoadMoreAnalysisScenarios,
    onCopyAnalysisResult: handleCopyAnalysisResult,
    onCopyAnalysisScenarios: handleCopyAnalysisScenarios,
    onDeleteAnalysisResult: handleDeleteAnalysisResult,
    onStartImpactAnalysis: handleStartImpactAnalysis,
    onSelectScenario: handleSelectScenario,
    onViewScenario: handleViewScenario,
    onViewResult: handleViewResult,
    pendingScenarioInitialData,
    onCreateImpactScenarioFromMonitoring: handleCreateImpactScenarioFromMonitoring,
    onClearPendingScenarioInitialData: handleClearPendingScenarioInitialData,
  } = useValueChainScenarioView({
    valueChainMapId,
    nodes,
    boardData,
    countryLookup,
    groupedDataRef,
    viewportZoomRef,
    setNodes,
    setEdges,
    closeAIFlow,
    onOpenFilters,
    setOpenFilters,
    setOpenAIGeneratingModal,
    setOpenAICreatorBoMUnits,
    setOpenAICreatorSupplierUnits,
  });

  const {
    selectedEdge,
    setSelectedEdge,
    setCurrentConnection,
    onConnect,
    onEdgeClick,
    onEdgeDoubleClick,
    onAddServiceConnection,
    isCreateLogicalConnectionLoading,
    isUpdateLogicalConnectionLoading,
    isDeleteLogicalConnectionLoading,
    isCreateSupplierConnectionLoading,
    isUpdateSupplierConnectionLoading,
    isDeleteSupplierConnectionLoading,
    isCreateServiceConnectionLoading,
    isUpdateServiceConnectionLoading,
    isDeleteServiceConnectionLoading,
    isCreateManufactureConnectionLoading,
    isUpdateManufactureConnectionLoading,
    isDeleteManufactureConnectionLoading,
  } = useValueChainConnections({
    edges,
    nodesRef,
    valueChainMapId,
    setActiveModal,
  });

  const { data: enumData, isLoading: isEnumLoading } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.volumeQtyUnit],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  useEffect(() => {
    if (!lastBomSocketPayload) {
      return;
    }

    if (!isAIGeneratingFloatingButtonEnabled) {
      return;
    }

    const payloadKey = JSON.stringify(lastBomSocketPayload);
    if (lastHandledBomSocketPayloadKeyRef.current === payloadKey) {
      return;
    }

    const hasMatchedSession = (vcaAgentSessions?.data || []).some(
      (sessionItem) => sessionItem.id === lastBomSocketPayload.sessionId,
    );

    if (!hasMatchedSession) {
      return;
    }

    const normalizedSocketStatus = normalizeAIGeneratingStatus(lastBomSocketPayload.status);

    if (
      normalizedSocketStatus === VALUE_CHAIN_AI_AGENT.STATUS_DONE
    ) {
      lastHandledBomSocketPayloadKeyRef.current = payloadKey;
      setIsSupplierGeneratingPending(false);
      setHasUnreadAIGeneratingResults(true);
    }
  }, [isAIGeneratingFloatingButtonEnabled, lastBomSocketPayload, vcaAgentSessions?.data]);

  useEffect(() => {
    if (!isAIGeneratingFloatingButtonEnabled) {
      hadBackgroundProcessingRef.current = false;
      return;
    }

    if (hadBackgroundProcessingRef.current && !hasProcessingAgentSessions) {
      setIsSupplierGeneratingPending(false);
      setHasUnreadAIGeneratingResults(true);
    }

    hadBackgroundProcessingRef.current = hasProcessingAgentSessions;
  }, [hasProcessingAgentSessions, isAIGeneratingFloatingButtonEnabled]);

  useEffect(() => {
    if (
      !isVCAgentSessionsLoaded
      || (!hasActiveAgentSessionProcessing && !hasProcessingAgentSessions)
    ) {
      return;
    }

    setIsAISocketLaunchRequested(true);
    setIsAIGeneratingFloatingButtonEnabled(true);
  }, [
    hasActiveAgentSessionProcessing,
    hasProcessingAgentSessions,
    isVCAgentSessionsLoaded,
  ]);

  useEffect(() => {
    const shouldPollSupplierDiscoverySession = openAICreatorSupplierUnits && isSupplierGeneratingPending;
    const shouldPollMonitoringSession = openAIGeneratingModal && isMonitoringSessionGenerating;

    if (
      !isSocketPollingFallbackActive
      || (!hasPollingProcessingStatuses && !shouldPollSupplierDiscoverySession && !shouldPollMonitoringSession)
    ) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      refetchVCAgentSessions();

      if (isDraftReviewModeEnabled && draftReviewSession?.id) {
        refetchDraftUnits();
      }
    }, VALUE_CHAIN_AI_AGENT.GENERATING_MODAL_POLLING_INTERVAL);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    draftReviewSession?.id,
    hasPollingProcessingStatuses,
    isDraftReviewModeEnabled,
    isMonitoringSessionGenerating,
    isSocketPollingFallbackActive,
    isSupplierGeneratingPending,
    openAICreatorSupplierUnits,
    openAIGeneratingModal,
    refetchDraftUnits,
    refetchVCAgentSessions,
  ]);

  useEffect(() => {
    if (
      !isSocketPollingFallbackActive
      || hasPollingProcessingStatuses
      || isVCAgentSessionsFetching
    ) {
      return;
    }

    setIsSupplierGeneratingPending(false);
    setIsSocketPollingFallbackActive(false);
  }, [
    hasPollingProcessingStatuses,
    isSocketPollingFallbackActive,
    isVCAgentSessionsFetching,
  ]);

  const aiGeneratingButtonHint = useMemo(() => {
    if (!hasUnreadAIGeneratingResults) {
      return '';
    }

    if (lastBomSocketPayload?.message?.trim()) {
      return t('valueChainMap.aiGeneratingNothingFoundShort');
    }

    return t('valueChainMap.aiGeneratingButtonHint');
  }, [
    hasUnreadAIGeneratingResults,
    lastBomSocketPayload?.message,
    t,
  ]);

  const { updateEdgeStyles } = useValueChainEdgeStyles(nodes);

  const closeModal = useCallback(() => {
    setSelectedBoard(undefined);
    setActiveModal(undefined);
    setSelectedEdge(undefined);
    setCurrentConnection(undefined);
    setNodes((prevNodes) => prevNodes.map((prevNode) => (prevNode.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD
      ? {
        ...prevNode,
        data: {
          ...prevNode.data,
          isDragTarget: false,
          isValidSectionHighlight: false,
          isInvalidDropTarget: false,
          dropZoneType: undefined,
          draggingNodeType: undefined,
          tempHeight: undefined,
          tempYOffset: undefined,
          tempTotalChildrenHeight: undefined,
        },
      }
      : prevNode)));
  }, [setNodes, setSelectedEdge, setCurrentConnection]);

  const {
    isDeleteModalOpen,
    setDeleteModalOpen,
    districtErrors,
    onUpdateSwimlaneOrderIndex,
    onAddSwimlane,
    onEditSwimlane,
    onDeleteSwimlane,
    onDeleteSwimlaneConfirm,
    onRenameSubmit,
    isUpdateBoardColumnLoading,
    isCreateBoardColumnLoading,
    isDeleteBoardColumnLoading,
  } = useValueChainSwimlanes({
    nodes,
    valueChainMapId,
    selectedBoard,
    setSelectedBoard,
    setActiveModal,
    closeModal,
    creatingTargetIdRef,
  });

  const {
    onNodeDrag: handleNodeDrag,
    onNodeDragStart: handleNodeDragStart,
    onNodeDragStop: handleNodeDragStop,
    onDragOver,
    onDrop,
    creatingTargetId,
  } = useDragAndDrop({
    nodes,
    setNodes,
    screenToFlowPosition,
    valueChainMapId,
    updateBoardUnit,
    onUpdateSwimlaneOrderIndex,
    panelType,
    setPanelType,
    setCreatingTargetId: () => {
      // This will be handled by the hook
    },
    setActiveModal,
    closeModal,
    setCurrentTargetSwimlaneId,
    openProfitCenterModal,
    setSelectedProfitCenter,
    openCustomerModal,
    setSelectedCustomer,
    openSupplierModal,
    setSelectedSupplier,
    openBalanceSheetAssetsModal,
    setSelectedBalanceSheetAssets,
    openInputProductModal,
    setSelectedInputProduct,
    hasProfitCenter,
    t,
    toast,
  });

  creatingTargetIdRef.current = creatingTargetId;

  const {
    isBoardEditingEnabled,
    isNodeEditingEnabled,
    isEdgeChangesEnabled,
    isValueChainLoading,
    isBoardContentHidden,
  } = useValueChainInteractionState({
    isAccessDisabled,
    isDraftReviewMode,
    isScenarioViewMode,
    openAICardPanel: openAICardPanel || openMonitoringResultsPanel || openScheduleConfigPanel
      || openScheduleRunHistoryPanel,
    isLoading,
    isLocationsCountriesFetching,
    isUpdateBoardColumnLoading,
    isCreateBoardColumnLoading,
    isDeleteBoardColumnLoading,
    isBusinessModelFetching,
    isEnumLoading,
    isCreateLogicalConnectionLoading,
    isUpdateLogicalConnectionLoading,
    isDeleteLogicalConnectionLoading,
    isCreateSupplierConnectionLoading,
    isUpdateSupplierConnectionLoading,
    isDeleteSupplierConnectionLoading,
    isCreateServiceConnectionLoading,
    isUpdateServiceConnectionLoading,
    isDeleteServiceConnectionLoading,
    isCreateManufactureConnectionLoading,
    isUpdateManufactureConnectionLoading,
    isDeleteManufactureConnectionLoading,
    isDraftUnitsLoading,
    isApplyDraftSessionLoading,
    isDeleteSessionLoading: isDeleteSessionLoading || isDeleteDraftSessionLoading,
    isActivateAgentSessionLoading,
    isTransitioningToDraftReview,
    isBoardRefetchingAfterApply,
    isDraftBoardBuilding,
  });

  useEffect(() => {
    const resetDraggingState = () => {
      isNodeDraggingRef.current = false;
    };

    window.addEventListener('mouseup', resetDraggingState);
    window.addEventListener('touchend', resetDraggingState);

    return () => {
      window.removeEventListener('mouseup', resetDraggingState);
      window.removeEventListener('touchend', resetDraggingState);
    };
  }, []);

  const onCloseAIAgentsSidebar = useCallback(() => {
    closeAIFlow();
  }, [closeAIFlow]);

  const onCloseAISessionsSidebar = useCallback(() => {
    closeAIFlow();
  }, [closeAIFlow]);

  const onCloseAICardPanel = useCallback(() => {
    closeAIFlow();
  }, [closeAIFlow]);

  const onBackToMonitoringHubFromCardPanel = useCallback(() => {
    setOpenAICardPanel(false);
    setOpenScheduleConfigPanel(false);
    setOpenMonitoringHubSidebar(true);
    setMonitoringCardPanelFlow('manual-session');
    setSelectedAICards([]);
  }, [setSelectedAICards]);

  const clearMonitoringMockTimer = useCallback(() => {
    if (!monitoringMockTimerRef.current) {
      return;
    }

    window.clearTimeout(monitoringMockTimerRef.current);
    monitoringMockTimerRef.current = null;
  }, []);

  const scheduleMonitoringMockCompletion = useCallback((
    targetSessionId: number,
    sessionType?: string,
  ) => {
    if (!shouldUseMonitoringMock(sessionType)) {
      return;
    }

    clearMonitoringMockTimer();
    monitoringMockTimerRef.current = window.setTimeout(() => {
      setExistingSessionStatusOverride({
        sessionId: targetSessionId,
        status: VALUE_CHAIN_AI_AGENT.STATUS_DONE,
      });
      monitoringMockTimerRef.current = null;
    }, 3000);
  }, [clearMonitoringMockTimer]);

  const launchMonitoringGeneratingUI = useCallback((
    session: ISessionVCItem,
    cards: IAICardSelection[],
  ) => {
    setActiveAgentKind(resolveMonitoringActiveAgentKind(session.type));
    setActiveAgentSession(session);
    setSessionId(session.id);
    setGeneratingAICards(cards);
    setSelectedAICards([]);
    setOpenAICardPanel(false);
    setOpenMonitoringHubSidebar(false);
    setOpenAIAgentsSidebar(false);
    setOpenMonitoringResultsPanel(false);
    setOpenScheduleRunHistoryPanel(false);
    setOpenAIGeneratingModal(true);
    setIsMonitoringGeneratingOverview(false);
    setIsAIGeneratingFloatingButtonEnabled(true);
    setIsAISocketLaunchRequested(true);
    setIsSocketPollingFallbackActive(true);
  }, [setGeneratingAICards, setSelectedAICards]);

  const openMonitoringGeneratingOverview = useCallback(() => {
    const firstSession = generatingMonitoringSessions[0]?.session;

    if (firstSession) {
      setActiveAgentKind(resolveMonitoringActiveAgentKind(firstSession.type));
    }

    setIsMonitoringGeneratingOverview(true);
    setGeneratingAICards([]);
    setOpenAICardPanel(false);
    setOpenMonitoringHubSidebar(false);
    setOpenAIAgentsSidebar(false);
    setOpenMonitoringResultsPanel(false);
    setOpenScheduleRunHistoryPanel(false);
    setOpenAIGeneratingModal(true);
    setIsAIGeneratingFloatingButtonEnabled(true);
  }, [generatingMonitoringSessions, setGeneratingAICards]);

  const openMonitoringResults = useCallback((session: ISessionVCItem, executionKey?: string) => {
    const selectedCards = getSelectedCardsFromSession(session);
    const fallbackEntities = selectedCards
      .map((card) => {
        const unitId = getUnitIdFromNodeId(card.nodeId);
        if (unitId === null) {
          return null;
        }

        return {
          unitId,
          name: card.name,
        };
      })
      .filter((entity): entity is { unitId: number, name: string } => Boolean(entity));
    const sessionAgentDefinition = getMonitoringAgentBySessionType(session.type)
      ?? REPUTATION_MONITORING_AGENT;
    const sessionTitle = buildMonitoringSessionTitle({
      session,
      agentDefinition: sessionAgentDefinition,
      formatTitle: (key) => t(key),
      formatDate: (dateValue) => (
        dateValue && dayjs(dateValue).isValid()
          ? dayjs(dateValue).format('MMM D')
          : ''
      ),
    });

    setActiveAgentKind(resolveMonitoringActiveAgentKind(session.type));
    setActiveAgentSession(session);
    setSessionId(session.id);
    setOpenAIGeneratingModal(false);
    setOpenMonitoringHubSidebar(false);
    setOpenAICardPanel(false);
    setGeneratingAICards([]);
    setMonitoringResultsSessionTitle(sessionTitle);
    setMonitoringResultsSessionId(session.id);
    setMonitoringResultsRunId(undefined);
    setMonitoringResultsExecutionKey(executionKey);
    setMonitoringResultsBreadcrumbMode('session');
    setMonitoringResultsScheduleTitle(undefined);
    setMonitoringResultsRunLabel(undefined);
    setMonitoringResultsFallbackEntities(fallbackEntities);
    setActiveMonitoringSchedule(undefined);
    setOpenMonitoringResultsPanel(false);
    setMonitoringHubActiveTab('sessions');
    setOpenScheduleRunHistoryPanel(true);
    setIsMonitoringGeneratingOverview(false);
  }, [getSelectedCardsFromSession, setGeneratingAICards, t]);

  const onViewMonitoringSessionResults = useCallback((session: ISessionVCItem) => {
    openMonitoringResults(session);
  }, [openMonitoringResults]);

  const onStartMonitoringSession = useCallback(async () => {
    if (!valueChainMapId || !existingMonitoringSession?.id || !selectedAICards.length) {
      return;
    }

    const sessionIdToStart = existingMonitoringSession.id;
    const { sessionType, subType: sessionSubType } = activeMonitoringAgent;
    const agentConfigId = monitoringAgentConfigId ?? existingMonitoringSession.agentConfigId ?? null;

    if (!parsedValueChainMapId) {
      return;
    }

    if (!agentConfigId) {
      toast.error(t('valueChainMap.agentTemplateRequired'));
      return;
    }

    const cards = selectedAICards;

    try {
      const response = await startVCAgentSession({
        valueChainMapId: parsedValueChainMapId,
        sessionId: sessionIdToStart,
        data: buildMonitoringStartPayload(sessionSubType, agentConfigId),
      }).unwrap();

      const startedSession = resolveMonitoringSessionAfterStart(
        existingMonitoringSession,
        response.data,
        sessionSubType,
      );

      launchMonitoringGeneratingUI(startedSession, cards);

      if (isMonitoringSessionProcessingStatus(startedSession.status)) {
        scheduleMonitoringMockCompletion(sessionIdToStart, sessionType);
      }

      Promise.resolve(refetchVCAgentSessions()).catch(() => {});
    } catch (error) {
      logMonitoringSessionStartError(sessionIdToStart, error, {
        type: sessionType,
        subType: sessionSubType,
      });

      if (shouldUseMonitoringMock(sessionType)) {
        const mockSession = resolveMonitoringSessionAfterStart(
          existingMonitoringSession,
          undefined,
          sessionSubType,
        );
        launchMonitoringGeneratingUI(mockSession, cards);
        scheduleMonitoringMockCompletion(sessionIdToStart, sessionType);
        return;
      }

      toast.error(extractApiErrorMessage(error) || t('notifications.valueChainStartSessionError'));
    }
  }, [
    activeMonitoringAgent,
    existingMonitoringSession,
    launchMonitoringGeneratingUI,
    monitoringAgentConfigId,
    parsedValueChainMapId,
    refetchVCAgentSessions,
    scheduleMonitoringMockCompletion,
    selectedAICards,
    startVCAgentSession,
    t,
    valueChainMapId,
  ]);

  const onBackFromMonitoringResultsPanel = useCallback(() => {
    setOpenMonitoringResultsPanel(false);

    if (monitoringResultsBreadcrumbMode === 'schedule-run') {
      setOpenScheduleRunHistoryPanel(true);
      return;
    }

    setOpenMonitoringHubSidebar(true);
  }, [monitoringResultsBreadcrumbMode]);

  const onCloseMonitoringResultsPanel = useCallback(() => {
    setOpenMonitoringResultsPanel(false);
    setActiveAgentKind(null);
    setMonitoringResultsSessionTitle(undefined);
    setMonitoringResultsSessionId(undefined);
    setMonitoringResultsRunId(undefined);
    setMonitoringResultsBreadcrumbMode('session');
    setMonitoringResultsScheduleTitle(undefined);
    setMonitoringResultsRunLabel(undefined);
    setMonitoringResultsFallbackEntities([]);
    setOpenScheduleRunHistoryPanel(false);
    setActiveMonitoringSchedule(undefined);
  }, []);

  const onCreateImpactScenarioFromMonitoringResults = useCallback(async (
    preferredUnitIds?: number[],
  ) => {
    const boardUnitIds = getBoardResolvableUnitIds(nodes);
    const resolvedEntities = resolveMonitoringEntitiesFromBoard(
      nodes,
      monitoringResultsFallbackEntities,
    );
    const monitoredOnBoard = resolvedEntities
      .map((entity) => entity.unitId)
      .filter((unitId) => boardUnitIds.has(unitId));
    const atRiskUnitIds = Array.from(getAtRiskUnitIds(
      monitoringResultsForBadges,
      monitoredOnBoard,
      resolvedEntities,
    ));

    const resolvePreferredUnitId = (preferredId: number): number | null => {
      if (boardUnitIds.has(preferredId)) {
        return preferredId;
      }

      if (atRiskUnitIds.includes(preferredId)) {
        return preferredId;
      }

      const preferredNameFromFallback = monitoringResultsFallbackEntities
        .find((entity) => entity.unitId === preferredId)
        ?.name;

      let preferredNameFromResults: string | undefined;

      if (isOwnershipMonitoringSignalResult(monitoringResultsForBadges)) {
        preferredNameFromResults = monitoringResultsForBadges.entities
          .find((entity) => entity.unitId === preferredId)
          ?.name;
      } else if (isFinanceMonitoringSignalResult(monitoringResultsForBadges)) {
        preferredNameFromResults = monitoringResultsForBadges.entities
          .find((entity) => entity.unitId === preferredId)
          ?.name;
      }

      const preferredName = preferredNameFromFallback || preferredNameFromResults;

      if (!preferredName?.trim()) {
        return null;
      }

      const matchedBoardEntity = resolvedEntities.find(
        (entity) => entity.name.trim().toLowerCase() === preferredName.trim().toLowerCase(),
      );

      return matchedBoardEntity?.unitId ?? null;
    };

    const preferredOnBoard = Array.from(new Set(
      (preferredUnitIds || [])
        .map(resolvePreferredUnitId)
        .filter((unitId): unitId is number => unitId != null && unitId > 0),
    ));
    const unitIds = preferredOnBoard.length ? preferredOnBoard : atRiskUnitIds;

    if (!unitIds.length) {
      return;
    }

    if (
      !preferredOnBoard.length
      && !shouldShowMonitoringImpactScenarioCta(monitoringResultsForBadges, unitIds)
    ) {
      return;
    }

    const getScenarioNameKey = () => {
      if (activeMonitoringAgent.resultsMode === 'regulations') {
        return 'valueChainMap.monitoring.regulation.impactBridgeScenarioName';
      }

      if (activeMonitoringAgent.resultsMode === 'financial-risk') {
        return 'valueChainMap.monitoring.finance.impactBridgeScenarioName';
      }

      if (activeMonitoringAgent.resultsMode === 'ownership') {
        return 'valueChainMap.monitoring.ownership.impactBridgeScenarioName';
      }

      return 'valueChainMap.monitoring.reputation.impactBridgeScenarioName';
    };

    const scenarioNameKey = getScenarioNameKey();
    const scenarioName = t(
      scenarioNameKey,
      {
        date: dayjs().format(VALUE_CHAIN_MAP_ANALYSIS_DATE_FORMAT),
      },
    );
    const initialData = buildMonitoringImpactScenarioInitialData({
      scenarioName,
      unitIds,
    });

    closeAIFlow();
    setOpenAIGeneratingModal(false);

    await handleCreateImpactScenarioFromMonitoring(initialData);
  }, [
    activeMonitoringAgent.resultsMode,
    closeAIFlow,
    handleCreateImpactScenarioFromMonitoring,
    monitoringResultsFallbackEntities,
    monitoringResultsForBadges,
    nodes,
    setOpenAIGeneratingModal,
    t,
  ]);

  useEffect(() => {
    if (
      !openAIGeneratingModal
      || isMonitoringGeneratingOverview
      || !isMonitoringAgentKind(activeAgentKind)
      || !effectiveMonitoringSession
    ) {
      return;
    }

    if (!isMonitoringSessionReadyForResults(effectiveMonitoringSession)) {
      return;
    }

    openMonitoringResults(effectiveMonitoringSession);
    clearMonitoringMockTimer();
    setExistingSessionStatusOverride(undefined);
  }, [
    activeAgentKind,
    clearMonitoringMockTimer,
    effectiveMonitoringSession,
    openAIGeneratingModal,
    openMonitoringResults,
    isMonitoringGeneratingOverview,
  ]);

  useEffect(() => () => {
    clearMonitoringMockTimer();
  }, [clearMonitoringMockTimer]);

  const onBackToAIAgentsFromMonitoringHub = useCallback(() => {
    setOpenMonitoringHubSidebar(false);
    setActiveAgentKind(null);
    setOpenAIAgentsSidebar(true);
  }, []);

  const onBackToAIAgentsSidebar = useCallback(() => {
    setOpenAISessionsSidebar(false);
    setOpenAICardPanel(false);
    setOpenAIAgentsSidebar(true);
  }, []);

  const onBackToAISessionsSidebar = useCallback(() => {
    setOpenAICardPanel(false);
    setOpenAISessionsSidebar(true);
    setSelectedAICards([]);
  }, [setSelectedAICards]);

  const onCloseAIGeneratingModal = useCallback(() => {
    setOpenAIGeneratingModal(false);
    setOpenAIAgentsSidebar(false);
    setOpenMonitoringHubSidebar(false);
    setIsMonitoringGeneratingOverview(false);
    setIsAIGeneratingFloatingButtonEnabled(true);
  }, []);

  const handleCloseModalBom = () => {
    setOpenAICreatorBoMUnits(false);
  };

  const handleCloseModalSupplier = () => {
    setOpenAICreatorSupplierUnits(false);
  };

  const onCloseAICreatorBoMUnits = useCallback(() => {
    setOpenAICreatorBoMUnits(false);
    setBomUnitsCards([]);
    setBomUnitsSession(undefined);
    setBomUnitsInitialTabNodeId(undefined);
    setOpenAIGeneratingModal(false);
    setGeneratingAICards([]);
  }, [setGeneratingAICards]);

  const onCloseAICreatorSupplierUnits = useCallback(() => {
    setOpenAICreatorSupplierUnits(false);
    setSupplierUnitsCards([]);
    setSupplierUnitsSession(undefined);
    setSupplierUnitsInitialTabNodeId(undefined);
    setOpenAIGeneratingModal(false);
    setGeneratingAICards([]);
  }, [setGeneratingAICards]);

  const onSearchSuppliersSuccess = useCallback((cards: IAICardSelection[], supplierSession?: ISessionVCItem) => {
    isSupplierDiscoveryGenerationRequestedRef.current = false;
    clearSocketNoMessageTimer();
    setIsAISocketLaunchRequested(false);
    setIsSocketPollingFallbackActive(false);
    setIsAIGeneratingFloatingButtonEnabled(false);
    setIsBomDraftFloatingButtonVisible(false);
    setIsSupplierGeneratingPending(false);
    setHasUnreadAIGeneratingResults(false);
    setIsDraftReviewModeEnabled(false);
    setDraftReviewSession(undefined);
    setDraftData(undefined);
    setCachedDraftData(undefined);
    setCachedDraftSessionId(undefined);
    setGeneratingAICards([]);
    setOpenAIGeneratingModal(false);
    setSupplierUnitsInitialTabNodeId(undefined);
    setSupplierUnitsCards(cards);
    const nextSupplierSession = supplierSession || existingSession;

    if (nextSupplierSession?.id) {
      setSessionId(nextSupplierSession.id);
      setActiveAgentSession(nextSupplierSession);
      setIgnoredExistingSessionId(undefined);
    }

    setSupplierUnitsSession(
      nextSupplierSession
        ? { ...nextSupplierSession, subType: VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE }
        : undefined,
    );
    setOpenAICreatorSupplierUnits(true);
  }, [
    clearSocketNoMessageTimer,
    existingSession,
    setCachedDraftData,
    setCachedDraftSessionId,
    setDraftData,
    setGeneratingAICards,
  ]);

  const onAnalyzeBom = useCallback((cards: IAICardSelection[], bomSession?: ISessionVCItem) => {
    const nextBomSession = bomSession || existingSession;
    const isBillOfMaterialsReviewStatus = nextBomSession?.subType
      === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE
      && (
        nextBomSession?.status === VALUE_CHAIN_AI_AGENT.STATUS_REVIEW
        || nextBomSession?.status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT
      );

    if (isBillOfMaterialsReviewStatus) {
      setGeneratingAICards(cards);
      setOpenAIGeneratingModal(true);
      return;
    }

    if (nextBomSession?.id) {
      setSessionId(nextBomSession.id);
      setActiveAgentSession(nextBomSession);
      setIgnoredExistingSessionId(undefined);
    }

    setIsDraftReviewModeEnabled(false);
    setDraftReviewSession(undefined);
    setDraftData(undefined);
    setCachedDraftData(undefined);
    setCachedDraftSessionId(undefined);
    setOpenAICreatorSupplierUnits(false);
    setSupplierUnitsCards([]);
    setSupplierUnitsSession(undefined);
    setSupplierUnitsInitialTabNodeId(undefined);
    setOpenAIGeneratingModal(false);
    setGeneratingAICards([]);
    setBomUnitsInitialTabNodeId(undefined);
    setBomUnitsCards(cards);
    setBomUnitsSession(
      nextBomSession
        ? { ...nextBomSession, subType: VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE }
        : undefined,
    );
    setOpenAICreatorBoMUnits(true);
  }, [
    existingSession,
    setCachedDraftData,
    setCachedDraftSessionId,
    setDraftData,
    setGeneratingAICards,
  ]);

  const onOpenGeneratingSession = useCallback((
    sessionData: IAIGeneratingModalSessionData,
    card?: IAICardSelection,
  ) => {
    const isDraftReviewStatus = sessionData.session.status === VALUE_CHAIN_AI_AGENT.STATUS_REVIEW
      || sessionData.session.status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT;
    const shouldHideFloatingButtonInBom = sessionData.session.subType
      === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE
      && !isDraftReviewStatus
      && !hasProcessingAgentSessions
      && !isBomDraftFloatingButtonVisible;
    const shouldHideFloatingButtonInSupplierDiscovery = sessionData.session.subType
      === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE
      && isDraftReviewStatus;

    setOpenAIGeneratingModal(false);
    setGeneratingAICards([]);
    setIsMonitoringGeneratingOverview(false);

    if (isMonitoringSessionType(sessionData.session.type)) {
      openMonitoringResults(sessionData.session);
      return;
    }

    if (sessionData.session.subType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE) {
      if (isDraftReviewStatus && !card) {
        hideAIGeneratingFloatingButton();
        setOpenAICreatorBoMUnits(false);
        setOpenAICreatorSupplierUnits(false);
        setBomUnitsCards([]);
        setBomUnitsSession(undefined);
        setBomUnitsInitialTabNodeId(undefined);
        setSupplierUnitsCards([]);
        setSupplierUnitsSession(undefined);
        setSupplierUnitsInitialTabNodeId(undefined);
        setDraftData(undefined);
        setCachedDraftData(undefined);
        setCachedDraftSessionId(undefined);
        setDraftReviewSession(sessionData.session);
        setSelectedAICards(sessionData.selectedCards);
        setIsDraftReviewModeEnabled(true);
        return;
      }

      setIsDraftReviewModeEnabled(false);
      setDraftReviewSession(undefined);
      setBomUnitsInitialTabNodeId(card?.nodeId);
      setBomUnitsCards(sessionData.selectedCards);
      setBomUnitsSession(sessionData.session);
      setOpenAICreatorSupplierUnits(false);
      setSupplierUnitsCards([]);
      setSupplierUnitsSession(undefined);
      setSupplierUnitsInitialTabNodeId(undefined);
      setOpenAICreatorBoMUnits(true);

      if (shouldHideFloatingButtonInBom) {
        hideAIGeneratingFloatingButton();
      }

      return;
    }

    if (sessionData.session.subType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE) {
      if (shouldHideFloatingButtonInSupplierDiscovery) {
        hideAIGeneratingFloatingButton();
      }

      setIsDraftReviewModeEnabled(false);
      setDraftReviewSession(undefined);
      setDraftData(undefined);
      setCachedDraftData(undefined);
      setCachedDraftSessionId(undefined);
      setOpenAICreatorBoMUnits(false);
      setBomUnitsCards([]);
      setBomUnitsSession(undefined);
      setBomUnitsInitialTabNodeId(undefined);
      setSupplierUnitsInitialTabNodeId(card?.nodeId);
      setSelectedAICards(sessionData.selectedCards);
      setSupplierUnitsCards(sessionData.selectedCards);
      setSupplierUnitsSession(sessionData.session);
      setOpenAICreatorSupplierUnits(true);
    }
  }, [
    hasProcessingAgentSessions,
    hideAIGeneratingFloatingButton,
    isBomDraftFloatingButtonVisible,
    setCachedDraftData,
    setCachedDraftSessionId,
    setDraftData,
    setGeneratingAICards,
    setSelectedAICards,
    openMonitoringResults,
  ]);

  const onBoMGenerateWithAI = useCallback(() => {
    socketFallbackDelayRef.current = DEFAULT_SOCKET_FALLBACK_DELAY;
    setIsAISocketLaunchRequested(true);
    setIsAIGeneratingFloatingButtonEnabled(true);
    setIsSupplierGeneratingPending(false);
    setIsBomDraftFloatingButtonVisible(true);
    setHasUnreadAIGeneratingResults(false);
  }, []);

  const onBoMAddRecord = useCallback(() => {}, []);

  const onBoMAddManufacturer = useCallback(() => {}, []);

  const onBoMConfirm = useCallback(() => {
    const normalizedBomReviewSession = (
      sessionCandidate?: ISessionVCItem,
    ): ISessionVCItem | undefined => {
      if (!sessionCandidate) {
        return undefined;
      }

      const isBillOfMaterialsSession = sessionCandidate.subType
        === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE;
      if (!isBillOfMaterialsSession) {
        return undefined;
      }

      return {
        ...sessionCandidate,
        status: VALUE_CHAIN_AI_AGENT.STATUS_REVIEW,
      };
    };

    const nextDraftReviewSession = normalizedBomReviewSession(existingSession)
      || normalizedBomReviewSession(liveBomUnitsSession);

    if (!nextDraftReviewSession) {
      return;
    }

    setOpenAIGeneratingModal(false);
    setGeneratingAICards([]);
    setDraftReviewSession(nextDraftReviewSession);
    setIsDraftReviewModeEnabled(true);
    setIsTransitioningToDraftReview(true);
    setExistingSessionStatusOverride({
      sessionId: nextDraftReviewSession.id,
      status: VALUE_CHAIN_AI_AGENT.STATUS_REVIEW,
    });
  }, [
    existingSession,
    liveBomUnitsSession,
    setGeneratingAICards,
    setIsTransitioningToDraftReview,
  ]);

  const onBoMProcessingTabsChange = useCallback((hasProcessingTabs: boolean) => {
    if (!isAIGeneratingFloatingButtonEnabled) {
      return;
    }

    setIsBomDraftFloatingButtonVisible(hasProcessingTabs);
  }, [isAIGeneratingFloatingButtonEnabled]);

  const onSupplierGenerateWithAI = useCallback(() => {
    isSupplierDiscoveryGenerationRequestedRef.current = true;
    socketFallbackDelayRef.current = SUPPLIER_DISCOVERY_SOCKET_FALLBACK_DELAY;
    setIsSocketPollingFallbackActive(false);
    setIsAISocketLaunchRequested(true);
    setIsAIGeneratingFloatingButtonEnabled(true);
    setIsSupplierGeneratingPending(true);
    setIsBomDraftFloatingButtonVisible(false);
    setHasUnreadAIGeneratingResults(false);
    startSocketPollingFallbackTimer(SUPPLIER_DISCOVERY_SOCKET_FALLBACK_DELAY);
  }, [startSocketPollingFallbackTimer]);

  const onSupplierAddRecord = useCallback(() => {}, []);

  const onSupplierConfirm = useCallback(() => {
    const normalizedSupplierReviewSession = (
      sessionCandidate?: ISessionVCItem,
    ): ISessionVCItem | undefined => {
      if (!sessionCandidate) {
        return undefined;
      }

      const isSupplierDiscoverySession = sessionCandidate.subType
        === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE;
      if (!isSupplierDiscoverySession) {
        return undefined;
      }

      return {
        ...sessionCandidate,
        status: VALUE_CHAIN_AI_AGENT.STATUS_REVIEW,
      };
    };

    const nextDraftReviewSession = normalizedSupplierReviewSession(existingSession)
      || normalizedSupplierReviewSession(liveSupplierUnitsSession);

    if (!nextDraftReviewSession) {
      return;
    }

    setOpenAIGeneratingModal(false);
    setGeneratingAICards([]);
    setDraftReviewSession(nextDraftReviewSession);
    setIsDraftReviewModeEnabled(true);
    setIsTransitioningToDraftReview(true);
    setExistingSessionStatusOverride({
      sessionId: nextDraftReviewSession.id,
      status: VALUE_CHAIN_AI_AGENT.STATUS_REVIEW,
    });
    setOpenAICreatorSupplierUnits(false);
  }, [
    existingSession,
    liveSupplierUnitsSession,
    setGeneratingAICards,
    setIsTransitioningToDraftReview,
  ]);

  const onSupplierProcessingTabsChange = useCallback((hasProcessingTabs: boolean) => {
    if (!isAIGeneratingFloatingButtonEnabled) {
      return;
    }

    setIsSupplierGeneratingPending(hasProcessingTabs);
  }, [isAIGeneratingFloatingButtonEnabled]);

  const onNodeDrag = useCallback((event: TNodeDragEvent, node: Node) => {
    if (!isNodeDraggingRef.current) {
      isNodeDraggingRef.current = true;
    }
    if (openAIAgentsSidebar || openAISessionsSidebar || openMonitoringHubSidebar) {
      closeAIFlow();
    }
    handleNodeDrag(event, node);
  }, [closeAIFlow, handleNodeDrag, openAIAgentsSidebar, openAISessionsSidebar, openMonitoringHubSidebar]);

  const onNodeDragStop = useCallback((event: TNodeDragEvent, node: Node) => {
    handleNodeDragStop(event, node);
    isNodeDraggingRef.current = false;
  }, [handleNodeDragStop]);

  const onNodeDragStart = useCallback((event: TNodeDragEvent, node: Node) => {
    if (openAIAgentsSidebar || openAISessionsSidebar || openMonitoringHubSidebar) {
      closeAIFlow();
    }
    isNodeDraggingRef.current = true;
    handleNodeDragStart(event, node);
  }, [closeAIFlow, handleNodeDragStart, openAIAgentsSidebar, openAISessionsSidebar, openMonitoringHubSidebar]);

  const { createNodesAndEdges } = useCreateNodesAndEdgesValueChain(
    onEditSwimlane,
    onDeleteSwimlane,
    isAccessDisabled,
  );

  useEffect(() => {
    prevNodesSnapshotRef.current = nodes;
    nodesRef.current = nodes;
  }, [nodes]);

  const { applyViewportCullingToDisplayNodes } = useValueChainBoardNodes({
    nodes,
    setNodes,
    setEdges,
    nodesRef,
    prevNodesSnapshotRef,
    groupedDataRef,
    boardData,
    isDraftReviewMode,
    isScenarioViewMode,
    isDraftBoardBuilding,
    isGroupingMode,
    isNodeDraggingRef,
    hasCompletedInitialLayoutRef,
    viewportSizeRef,
    viewportYRef,
    viewportZoomRef,
    createNodesAndEdges,
    applyAIPanelStateToNodes: applyCanvasUnitStateToNodes,
  });

  // When user starts dragging from sidebar (panelType set), highlight the valid board immediately
  useEffect(() => {
    if (
      !panelType
      || panelType === VALUE_CHAIN_PANEL_NODES_TYPES.FILTER
      || panelType === VALUE_CHAIN_PANEL_NODES_TYPES.SWIMLANE
    ) {
      return undefined;
    }
    const section = getSectionForPanelType(panelType);
    setNodes((prevNodes) => {
      const validBoards = section
        ? prevNodes.filter(
          (prevNode) => prevNode.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD
            && typeof (prevNode.data as { name?: string }).name === 'string'
            && isSectionValidForPanelType(
              (prevNode.data as { name?: string }).name as string,
              panelType,
            ),
        )
        : [];

      if (!validBoards.length) {
        return prevNodes;
      }

      const validBoardIds = new Set(validBoards.map((board) => board.id));

      return prevNodes.map((prevNode) => (prevNode.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD
        ? {
          ...prevNode,
          data: {
            ...prevNode.data,
            isValidSectionHighlight: validBoardIds.has(prevNode.id),
            isInvalidDropTarget: false,
          },
        }
        : prevNode));
    });
    return () => {
      setNodes((prevNodes) => prevNodes.map((prevNode) => (prevNode.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD
        ? { ...prevNode, data: { ...prevNode.data, isValidSectionHighlight: false, isInvalidDropTarget: false } }
        : prevNode)));
    };
  }, [panelType, setNodes]);

  const getBoardNameBySwimlaneId = useCallback((swimlaneId: string, nodesData: Node[]): string | undefined => {
    if (!swimlaneId) {
      return undefined;
    }

    const swimlaneNode = nodesData.find(
      (nodeItem) => nodeItem.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE && nodeItem.id === swimlaneId,
    );

    if (!swimlaneNode?.data?.boardId) {
      return undefined;
    }

    const boardNode = nodesData.find(
      (nodeItem) => nodeItem.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD
        && Number(nodeItem.data?.id) === Number(swimlaneNode.data?.boardId),
    );

    return typeof boardNode?.data?.name === 'string' ? boardNode.data.name : undefined;
  }, []);

  const visibleAISessions = useMemo<ISessionVCItem[]>(() => {
    if (isAISessionOpeningInEditMode) {
      return [];
    }

    const nextSessions = vcaAgentSessions?.data || [];

    if (!sessionIdOpeningInEditMode) {
      return nextSessions;
    }

    return nextSessions.filter((sessionItem) => sessionItem.id !== sessionIdOpeningInEditMode);
  }, [isAISessionOpeningInEditMode, sessionIdOpeningInEditMode, vcaAgentSessions?.data]);

  const visibleMonitoringSessions = useMemo<ISessionVCItem[]>(() => {
    if (isAISessionOpeningInEditMode) {
      return [];
    }

    const nextSessions = vcaAgentSessions?.data || [];

    if (!sessionIdOpeningInEditMode) {
      return nextSessions;
    }

    return nextSessions.filter((sessionItem) => sessionItem.id !== sessionIdOpeningInEditMode);
  }, [isAISessionOpeningInEditMode, sessionIdOpeningInEditMode, vcaAgentSessions?.data]);

  useEffect(() => {
    if (!pendingAppliedBomUnitIds.length || !valueChainMapId) {
      return;
    }

    const nextSelectedCards = getSelectedAICardsFromUnitIds(pendingAppliedBomUnitIds);

    if (nextSelectedCards.length !== pendingAppliedBomUnitIds.length) {
      return;
    }

    setSelectedAICards(nextSelectedCards);
    setSessionId(undefined);
    setActiveAgentSession(undefined);
    setOpenAIAgentsSidebar(false);
    setOpenAICardPanel(true);
    setPendingAppliedBomUnitIds([]);
  }, [
    getSelectedAICardsFromUnitIds,
    pendingAppliedBomUnitIds,
    setSelectedAICards,
    valueChainMapId,
  ]);

  const onOpenAIGeneratingModal = useCallback(() => {
    const isSupplierDiscoveryDraftReady = existingSession?.subType
      === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE
      && isDraftReviewReadyStatus(existingSession?.status)
      && hasUnreadAIGeneratingResults;

    if (isSupplierDiscoveryDraftReady && existingSession) {
      setHasUnreadAIGeneratingResults(false);
      openSessionReviewResults(existingSession);
      return;
    }

    if (generatingMonitoringSessions.length > 1) {
      openMonitoringGeneratingOverview();
      return;
    }

    const monitoringSessions = (vcaAgentSessions?.data || []).filter(
      (sessionItem) => isMonitoringSessionType(sessionItem.type),
    );
    const boundMonitoringSession = monitoringSessions.find(
      (sessionItem) => sessionItem.id === sessionId,
    ) || effectiveMonitoringSession;
    const processingMonitoringSession = monitoringSessions.find(
      (sessionItem) => isMonitoringSessionProcessingStatus(sessionItem.status),
    );
    const completedMonitoringSession = monitoringSessions.find(
      (sessionItem) => isMonitoringSessionReadyForResults(sessionItem),
    );
    const monitoringSession = boundMonitoringSession
      || processingMonitoringSession
      || completedMonitoringSession;

    if (monitoringSession) {
      if (isMonitoringSessionReadyForResults(monitoringSession)) {
        setHasUnreadAIGeneratingResults(false);
        openMonitoringResults(monitoringSession);
        return;
      }

      if (isMonitoringSessionProcessingStatus(monitoringSession.status)) {
        launchMonitoringGeneratingUI(
          monitoringSession,
          getSelectedCardsFromSession(monitoringSession),
        );
        return;
      }
    }

    const sessionUnitIds = existingSession?.selectedUnits?.map(({ unitId }) => unitId) || [];
    const nextGeneratingCards = selectedAICards.length
      ? selectedAICards
      : getSelectedAICardsFromUnitIds(sessionUnitIds);

    if (!nextGeneratingCards.length && generatingAISessions.length === 0) {
      return;
    }

    setGeneratingAICards(nextGeneratingCards);
    setOpenAIGeneratingModal(true);
  }, [
    effectiveMonitoringSession,
    existingSession,
    generatingAISessions.length,
    generatingMonitoringSessions.length,
    getSelectedAICardsFromUnitIds,
    getSelectedCardsFromSession,
    hasUnreadAIGeneratingResults,
    launchMonitoringGeneratingUI,
    openMonitoringGeneratingOverview,
    openMonitoringResults,
    openSessionReviewResults,
    selectedAICards,
    sessionId,
    setGeneratingAICards,
    vcaAgentSessions?.data,
  ]);

  const onOpenAIAgentsSidebar = useCallback(() => {
    const hasOwnOperationsCard = nodes.some((nodeItem) => {
      if (nodeItem.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
        return false;
      }

      const nodeData = nodeItem.data as unknown as ICustomUnitNodeData;
      const boardName = getBoardNameBySwimlaneId(String(nodeData.swimlaneId || ''), nodes);

      return boardName === VC_BOARD_TYPES.OWN_OPERATIONS
        && (
          nodeData.type === VALUE_CHAIN_UNIT_TYPES.OWN_OPERATION
          || nodeData.type === VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET
        );
    });

    if (!hasOwnOperationsCard) {
      toast.error(
        <span>
          {t('valueChainMap.aiPanelMissingCardsError')}
          {' '}
          <a className={styles.notificationLink} href={AppRoutes.balanceSheetAssets}>
            {t('valueChainMap.aiPanelBalanceSheetAssetsLink')}
          </a>
        </span>,
      );
      return;
    }

    onResetImpactScenariosView();
    setOpenFilters(false);
    setActiveAgentKind(null);
    setOpenAIAgentsSidebar(true);
    setOpenAISessionsSidebar(false);
    setOpenMonitoringHubSidebar(false);
    setOpenAICardPanel(false);
    setOpenAIGeneratingModal(false);
    setOpenAICreatorBoMUnits(false);
    setOpenAICreatorSupplierUnits(false);
  }, [getBoardNameBySwimlaneId, nodes, onResetImpactScenariosView, setOpenFilters, t]);

  const onOpenSupplierDiscoveryPanel = useCallback(() => {
    setActiveAgentKind('n-tier');
    setOpenAIAgentsSidebar(false);
    setOpenMonitoringHubSidebar(false);
    setOpenAISessionsSidebar(true);
    setOpenAICardPanel(false);
    setOpenAIGeneratingModal(false);
    setOpenAICreatorBoMUnits(false);
    setOpenAICreatorSupplierUnits(false);
    setIsDraftReviewModeEnabled(false);
    setDraftReviewSession(undefined);
    Promise.resolve(refetchVCAgentSessions()).catch(() => {});
  }, [refetchVCAgentSessions]);

  const openMonitoringAgentHub = useCallback((
    kind: Exclude<TValueChainActiveAgentKind, 'n-tier' | null>,
  ) => {
    setActiveAgentKind(kind);
    setMonitoringCardPanelFlow('manual-session');
    setMonitoringHubActiveTab('sessions');
    setSessionId(undefined);
    setActiveAgentSession(undefined);
    setSelectedAICards([]);
    setMonitoringAgentConfigId(null);
    setMonitoringResultsSessionId(undefined);
    setOpenAIAgentsSidebar(false);
    setOpenAISessionsSidebar(false);
    setOpenMonitoringHubSidebar(true);
    setOpenAICardPanel(false);
    setOpenAIGeneratingModal(false);
    setOpenAICreatorBoMUnits(false);
    setOpenAICreatorSupplierUnits(false);
    setIsDraftReviewModeEnabled(false);
    setDraftReviewSession(undefined);
    Promise.resolve(refetchVCAgentSessions()).catch(() => {});
  }, [refetchVCAgentSessions, setSelectedAICards]);

  const onOpenReputationMonitoring = useCallback(() => {
    openMonitoringAgentHub('reputation-monitoring');
  }, [openMonitoringAgentHub]);

  const onOpenRegulationMonitoring = useCallback(() => {
    openMonitoringAgentHub('regulation-monitoring');
  }, [openMonitoringAgentHub]);

  const onOpenFinanceMonitoring = useCallback(() => {
    openMonitoringAgentHub('finance-monitoring');
  }, [openMonitoringAgentHub]);

  const onOpenOwnershipMonitoring = useCallback(() => {
    openMonitoringAgentHub('ownership-monitoring');
  }, [openMonitoringAgentHub]);

  const onOpenClimateMonitoring = useCallback(() => {
    openMonitoringAgentHub('climate-monitoring');
  }, [openMonitoringAgentHub]);

  useEffect(() => {
    if (!isMonitoringAgentKind(activeAgentKind) || !sessionId) {
      return;
    }

    const boundSession = (vcaAgentSessions?.data || []).find(
      (sessionItem) => sessionItem.id === sessionId,
    ) || (activeAgentSession?.id === sessionId ? activeAgentSession : undefined);

    if (boundSession && boundSession.type !== activeMonitoringAgent.sessionType) {
      setSessionId(undefined);
      setActiveAgentSession(undefined);
      setSelectedAICards([]);
    }
  }, [
    activeAgentKind,
    activeAgentSession,
    activeMonitoringAgent.sessionType,
    sessionId,
    setSelectedAICards,
    vcaAgentSessions?.data,
  ]);

  useEffect(() => {
    if (
      !launchpadDiscoverEntry
      || launchpadDiscoverHandledRef.current
      || isLoading
      || !valueChainMapId
    ) {
      return undefined;
    }

    launchpadDiscoverHandledRef.current = true;

    const timerId = window.setTimeout(() => {
      onOpenSupplierDiscoveryPanel();
    }, 400);

    return () => window.clearTimeout(timerId);
  }, [
    isLoading,
    launchpadDiscoverEntry,
    onOpenSupplierDiscoveryPanel,
    valueChainMapId,
  ]);

  useEffect(() => {
    if (
      openAISessionsSidebar
      || openAICardPanel
      || (!sessionIdOpeningInEditMode && !isAISessionOpeningInEditMode)
    ) {
      return;
    }

    setSessionIdOpeningInEditMode(undefined);
    setIsAISessionOpeningInEditMode(false);
  }, [isAISessionOpeningInEditMode, openAICardPanel, openAISessionsSidebar, sessionIdOpeningInEditMode]);

  const resetAIAgentSessionPanels = useCallback(() => {
    setOpenAIAgentsSidebar(false);
    setOpenAISessionsSidebar(false);
    setOpenMonitoringHubSidebar(false);
    setOpenAICardPanel(false);
    setOpenAIGeneratingModal(false);
    setOpenAICreatorBoMUnits(false);
    setOpenAICreatorSupplierUnits(false);
    setGeneratingAICards([]);
    setDraftReviewSession(undefined);
    setIsDraftReviewModeEnabled(false);
    setDraftData(undefined);
    setCachedDraftData(undefined);
    setCachedDraftSessionId(undefined);
    setBomUnitsCards([]);
    setBomUnitsSession(undefined);
    setBomUnitsInitialTabNodeId(undefined);
    setSupplierUnitsCards([]);
    setSupplierUnitsSession(undefined);
    setSupplierUnitsInitialTabNodeId(undefined);
    setSessionId(undefined);
    setActiveAgentSession(undefined);
    setNTierLaneSubType(null);
  }, [
    setCachedDraftData,
    setCachedDraftSessionId,
    setDraftData,
    setGeneratingAICards,
  ]);

  const resetMonitoringSessionFlowPanels = useCallback(() => {
    setOpenAIAgentsSidebar(false);
    setOpenAISessionsSidebar(false);
    setOpenMonitoringHubSidebar(false);
    setOpenMonitoringResultsPanel(false);
    setOpenScheduleConfigPanel(false);
    setOpenScheduleRunHistoryPanel(false);
    setActiveMonitoringSchedule(undefined);
    setEditingMonitoringSchedule(undefined);
    setMonitoringCardPanelFlow('manual-session');
    setMonitoringAgentConfigId(null);
    setMonitoringResultsRunId(undefined);
    setMonitoringResultsBreadcrumbMode('session');
    setMonitoringResultsScheduleTitle(undefined);
    setMonitoringResultsRunLabel(undefined);
    setMonitoringResultsSessionTitle(undefined);
    setMonitoringResultsSessionId(undefined);
    setMonitoringResultsFallbackEntities([]);
    setOpenAIGeneratingModal(false);
    setOpenAICreatorBoMUnits(false);
    setOpenAICreatorSupplierUnits(false);
    setGeneratingAICards([]);
    setBomUnitsCards([]);
    setBomUnitsSession(undefined);
    setSupplierUnitsCards([]);
    setSupplierUnitsSession(undefined);
    setIsNewMonitoringSelection(false);
  }, [setGeneratingAICards]);

  const onCreateMonitoringScheduleSelection = useCallback(() => {
    resetMonitoringSessionFlowPanels();
    setActiveAgentKind((current) => (
      isMonitoringAgentKind(current) ? current : 'reputation-monitoring'
    ));
    setEditingMonitoringSchedule(undefined);
    setMonitoringCardPanelFlow('new-schedule');
    setMonitoringHubActiveTab('schedules');
    setSessionId(undefined);
    setActiveAgentSession(undefined);
    setSelectedAICards([]);
    setMonitoringAgentConfigId(null);
    setOpenMonitoringHubSidebar(false);
    setOpenAICardPanel(true);
  }, [resetMonitoringSessionFlowPanels, setSelectedAICards]);

  const onContinueToSchedule = useCallback(() => {
    if (!selectedAICards.length) {
      return;
    }

    setEditingMonitoringSchedule(undefined);
    setOpenAICardPanel(false);
    setOpenScheduleConfigPanel(true);
  }, [selectedAICards]);

  const onBackFromScheduleConfig = useCallback(() => {
    setOpenScheduleConfigPanel(false);

    if (editingMonitoringSchedule) {
      setEditingMonitoringSchedule(undefined);
      setMonitoringHubActiveTab('schedules');
      setOpenMonitoringHubSidebar(true);
      return;
    }

    setOpenAICardPanel(true);
  }, [editingMonitoringSchedule]);

  const onCloseScheduleConfigPanel = useCallback(() => {
    closeAIFlow();
  }, [closeAIFlow]);

  const onSaveMonitoringSchedule = useCallback(async (draft: IScheduleConfigDraft) => {
    if (!parsedValueChainMapId) {
      return;
    }

    const agentConfigId = draft.agentConfigId
      ?? editingMonitoringSchedule?.agentConfigId
      ?? null;

    if (!agentConfigId) {
      toast.error(t('valueChainMap.agentTemplateRequired'));
      return;
    }

    const schedulePayload = {
      mode: draft.mode,
      timezone: draft.timezone,
      recurrence: {
        ...draft.recurrence,
        timeOfDay: normalizeTimeOfDay(draft.recurrence.timeOfDay),
      },
    };

    if (editingMonitoringSchedule) {
      const title = buildMonitoringScheduleTitle({
        entities: editingMonitoringSchedule.entities || [],
        frequency: draft.recurrence.frequency,
        selectionMode: activeMonitoringAgent.selectionMode,
        resultsMode: activeMonitoringAgent.resultsMode,
      });

      try {
        await updateMonitoringSchedule({
          valueChainMapId: parsedValueChainMapId,
          scheduleId: editingMonitoringSchedule.id,
          agentConfigId,
          title,
          unitIds: editingMonitoringSchedule.unitIds,
          schedule: schedulePayload,
          autoCreateImpactScenario: draft.autoCreateImpactScenario,
          impactScenarioMinSeverity: draft.autoCreateImpactScenario
            ? draft.impactScenarioMinSeverity
            : null,
        }).unwrap();

        setOpenScheduleConfigPanel(false);
        setEditingMonitoringSchedule(undefined);
        setMonitoringHubActiveTab('schedules');
        setOpenMonitoringHubSidebar(true);
      } catch (error) {
        toast.error(
          extractApiErrorMessage(error) || t('valueChainMap.monitoringScheduleUpdateError'),
        );
      }

      return;
    }

    if (!selectedAICards.length) {
      return;
    }

    const entities = selectedAICards.flatMap((card) => {
      const unitId = getUnitIdFromNodeId(card.nodeId);

      if (!unitId) {
        return [];
      }

      return [{
        unitId,
        name: card.name,
      }];
    });

    if (!entities.length) {
      return;
    }

    const title = buildMonitoringScheduleTitle({
      entities,
      frequency: draft.recurrence.frequency,
      selectionMode: activeMonitoringAgent.selectionMode,
      resultsMode: activeMonitoringAgent.resultsMode,
    });

    try {
      await createMonitoringSchedule({
        valueChainMapId: parsedValueChainMapId,
        sessionType: activeMonitoringAgent.sessionType,
        subType: activeMonitoringAgent.subType,
        agentConfigId,
        unitIds: entities.map((entity) => entity.unitId),
        entities,
        title,
        schedule: schedulePayload,
        autoCreateImpactScenario: draft.autoCreateImpactScenario,
        impactScenarioMinSeverity: draft.autoCreateImpactScenario
          ? draft.impactScenarioMinSeverity
          : null,
      }).unwrap();

      setOpenScheduleConfigPanel(false);
      setOpenAICardPanel(false);
      setSelectedAICards([]);
      setMonitoringAgentConfigId(null);
      setMonitoringCardPanelFlow('manual-session');
      setMonitoringHubActiveTab('schedules');
      setOpenMonitoringHubSidebar(true);
    } catch (error) {
      toast.error(
        extractApiErrorMessage(error) || t('valueChainMap.monitoringScheduleSaveError'),
      );
    }
  }, [
    activeMonitoringAgent.resultsMode,
    activeMonitoringAgent.selectionMode,
    activeMonitoringAgent.sessionType,
    activeMonitoringAgent.subType,
    createMonitoringSchedule,
    editingMonitoringSchedule,
    parsedValueChainMapId,
    selectedAICards,
    setSelectedAICards,
    t,
    updateMonitoringSchedule,
  ]);

  const onOpenMonitoringSchedule = useCallback((schedule: IMonitoringSchedule) => {
    setActiveAgentKind(resolveMonitoringActiveAgentKind(schedule.sessionType));
    setActiveMonitoringSchedule(schedule);
    setEditingMonitoringSchedule(undefined);
    setMonitoringResultsSessionId(undefined);
    setMonitoringResultsSessionTitle(undefined);
    setOpenMonitoringHubSidebar(false);
    setOpenAICardPanel(false);
    setOpenScheduleConfigPanel(false);
    setOpenScheduleRunHistoryPanel(true);
  }, []);

  const onEditMonitoringSchedule = useCallback((schedule: IMonitoringSchedule) => {
    setActiveAgentKind(resolveMonitoringActiveAgentKind(schedule.sessionType));
    setEditingMonitoringSchedule(schedule);
    setActiveMonitoringSchedule(undefined);
    setOpenMonitoringHubSidebar(false);
    setOpenAICardPanel(false);
    setOpenScheduleRunHistoryPanel(false);
    setOpenScheduleConfigPanel(true);
  }, []);

  const onDeleteMonitoringSchedule = useCallback(async (schedule: IMonitoringSchedule) => {
    if (!parsedValueChainMapId) {
      return;
    }

    try {
      await deleteMonitoringSchedule({
        valueChainMapId: parsedValueChainMapId,
        scheduleId: schedule.id,
      }).unwrap();

      if (activeMonitoringSchedule?.id === schedule.id) {
        setOpenScheduleRunHistoryPanel(false);
        setActiveMonitoringSchedule(undefined);
        setOpenMonitoringHubSidebar(true);
        setMonitoringHubActiveTab('schedules');
      }
    } catch (error) {
      toast.error(
        extractApiErrorMessage(error) || t('valueChainMap.monitoringScheduleDeleteError'),
      );
    }
  }, [
    activeMonitoringSchedule?.id,
    deleteMonitoringSchedule,
    parsedValueChainMapId,
    t,
  ]);

  const onPauseMonitoringSchedule = useCallback(async (schedule: IMonitoringSchedule) => {
    if (!parsedValueChainMapId) {
      return;
    }

    try {
      await pauseMonitoringSchedule({
        valueChainMapId: parsedValueChainMapId,
        scheduleId: schedule.id,
      }).unwrap();
    } catch (error) {
      toast.error(
        extractApiErrorMessage(error) || t('valueChainMap.monitoringSchedulePauseError'),
      );
    }
  }, [parsedValueChainMapId, pauseMonitoringSchedule, t]);

  const onResumeMonitoringSchedule = useCallback(async (schedule: IMonitoringSchedule) => {
    if (!parsedValueChainMapId) {
      return;
    }

    try {
      await resumeMonitoringSchedule({
        valueChainMapId: parsedValueChainMapId,
        scheduleId: schedule.id,
      }).unwrap();
    } catch (error) {
      toast.error(
        extractApiErrorMessage(error) || t('valueChainMap.monitoringScheduleResumeError'),
      );
    }
  }, [parsedValueChainMapId, resumeMonitoringSchedule, t]);

  const onRunMonitoringScheduleNow = useCallback(async () => {
    if (!parsedValueChainMapId || !activeMonitoringSchedule) {
      return;
    }

    try {
      await runMonitoringScheduleNow({
        valueChainMapId: parsedValueChainMapId,
        scheduleId: activeMonitoringSchedule.id,
      }).unwrap();
      toast.success(t('valueChainMap.monitoringScheduleRunNowSuccess'));
    } catch (error) {
      toast.error(
        extractApiErrorMessage(error) || t('valueChainMap.monitoringScheduleRunNowError'),
      );
    }
  }, [activeMonitoringSchedule, parsedValueChainMapId, runMonitoringScheduleNow, t]);

  const onBackFromScheduleRunHistory = useCallback(() => {
    const isSessionHistory = Boolean(monitoringResultsSessionId) && !activeMonitoringSchedule;
    const isMonitoringStillGenerating = isSessionHistory && isMonitoringSessionGenerating;

    setOpenScheduleRunHistoryPanel(false);
    setActiveMonitoringSchedule(undefined);
    setMonitoringResultsSessionId(undefined);
    setMonitoringResultsSessionTitle(undefined);

    if (isMonitoringStillGenerating) {
      const processingMonitoringCount = generatingMonitoringSessions.filter(
        (sessionData) => isMonitoringSessionProcessingStatus(sessionData.session.status),
      ).length;

      if (processingMonitoringCount > 1) {
        openMonitoringGeneratingOverview();
        return;
      }

      setIsAIGeneratingFloatingButtonEnabled(true);
      setOpenMonitoringHubSidebar(false);
      setOpenAIAgentsSidebar(false);
      return;
    }

    setMonitoringHubActiveTab(isSessionHistory ? 'sessions' : 'schedules');
    setOpenMonitoringHubSidebar(true);
  }, [
    activeMonitoringSchedule,
    generatingMonitoringSessions,
    isMonitoringSessionGenerating,
    monitoringResultsSessionId,
    openMonitoringGeneratingOverview,
  ]);

  const onCloseScheduleRunHistoryPanel = useCallback(() => {
    closeAIFlow();
  }, [closeAIFlow]);

  const activateAISession = useCallback(async (
    sessionItem: ISessionVCItem,
  ): Promise<ISessionVCItem | undefined> => {
    if (!valueChainMapId) {
      return undefined;
    }

    const response = await activateAgentSession({
      valueChainMapId,
      sessionId: sessionItem.id,
    }).unwrap();

    return response.data || sessionItem;
  }, [activateAgentSession, valueChainMapId]);

  const onCreateAISessionSelection = useCallback(() => {
    resetAIAgentSessionPanels();
    setActiveAgentKind(null);
    setSelectedAICards([]);
    setSessionId(undefined);
    setActiveAgentSession(undefined);
    setIsNewNTierSelection(true);
    setIsNewMonitoringSelection(false);
    setNTierLaneSubType(null);
    setOpenAISessionsSidebar(false);
    setOpenAICardPanel(true);
  }, [
    resetAIAgentSessionPanels,
    setSelectedAICards,
  ]);

  const onEditAISession = useCallback(async (sessionItem: ISessionVCItem) => {
    try {
      const activatedSession = await activateAISession(sessionItem);

      if (!activatedSession) {
        return;
      }

      const nextSelectedCards = getSelectedCardsFromSession(activatedSession);

      resetAIAgentSessionPanels();
      setIsNewNTierSelection(false);
      setNTierLaneSubType(activatedSession.subType ?? null);
      setActiveAgentSession(activatedSession);
      setSessionId(activatedSession.id);
      setSelectedAICards(nextSelectedCards);
      setOpenAICardPanel(true);
    } catch (error) {
      toast.error(extractApiErrorMessage(error) || t('notifications.valueChainStartSessionError'));
    }
  }, [
    activateAISession,
    getSelectedCardsFromSession,
    resetAIAgentSessionPanels,
    setSelectedAICards,
    t,
  ]);

  // The assistant opens a session in the state it is in, as the agent panel does. Not started
  // yet: the unit selection panel, as a click in the Sessions list. Started: it becomes the one
  // the tab works on, as after "Search Suppliers" or "Analyze BOM", and its modal opens as from a
  // session click (a BOM session in review opens its draft review there).
  const onOpenAgentSessionFromAssistant = useCallback((session: ISessionVCItem) => {
    onResetImpactScenariosView();

    if (session.status === VALUE_CHAIN_AI_AGENT.STATUS_UNIT_SELECTION) {
      onEditAISession(session).catch(() => {});
      return;
    }

    setSessionId(session.id);
    setActiveAgentSession(session);
    setIgnoredExistingSessionId(undefined);
    onOpenGeneratingSession({ session, selectedCards: getSelectedCardsFromSession(session) });
  }, [getSelectedCardsFromSession, onEditAISession, onOpenGeneratingSession, onResetImpactScenariosView]);

  useValueChainAgentSessionAiAction({
    valueChainMapId,
    agentSessions: vcaAgentSessions?.data,
    isAgentSessionsFetching: isVCAgentSessionsFetching,
    agentSessionsFulfilledTimeStamp,
    refetchAgentSessions: refetchVCAgentSessions,
    isBoardReady: isInitialViewportReady,
    onOpenSession: onOpenAgentSessionFromAssistant,
  });

  const onDeleteAISession = useCallback((sessionItem: ISessionVCItem) => {
    if (!valueChainMapId) {
      return;
    }

    deleteSession({
      valueChainMapId,
      sessionId: sessionItem.id,
    })
      .unwrap()
      .then(() => {
        const isDeletedSessionActive = sessionId === sessionItem.id
          || activeAgentSession?.id === sessionItem.id;

        if (isDeletedSessionActive) {
          setSessionId(undefined);
          setActiveAgentSession(undefined);
          setSelectedAICards([]);
          setBomUnitsCards([]);
          setBomUnitsSession(undefined);
          setSupplierUnitsCards([]);
          setSupplierUnitsSession(undefined);
        }

        toast.success(t('valueChainMap.aiSessionsDeleted'));
        refetchVCAgentSessions();
      })
      .catch((error) => {
        toast.error(extractApiErrorMessage(error) || t('common.unknownError'));
      });
  }, [
    activeAgentSession?.id,
    deleteSession,
    refetchVCAgentSessions,
    sessionId,
    setSelectedAICards,
    t,
    valueChainMapId,
  ]);

  const onCreateMonitoringSessionSelection = useCallback(() => {
    resetMonitoringSessionFlowPanels();
    setActiveAgentKind((current) => (
      isMonitoringAgentKind(current) ? current : 'reputation-monitoring'
    ));
    setMonitoringCardPanelFlow('manual-session');
    setSessionId(undefined);
    setActiveAgentSession(undefined);
    setSelectedAICards([]);
    setMonitoringAgentConfigId(null);
    setIsNewMonitoringSelection(true);
    setOpenMonitoringHubSidebar(false);
    setOpenAICardPanel(true);
  }, [
    resetMonitoringSessionFlowPanels,
    setSelectedAICards,
  ]);

  const onEditMonitoringSession = useCallback(async (sessionItem: ISessionVCItem) => {
    try {
      const activatedSession = await activateAISession(sessionItem);

      if (!activatedSession) {
        return;
      }

      const nextSelectedCards = getSelectedCardsFromSession(activatedSession);

      resetMonitoringSessionFlowPanels();
      setActiveAgentKind(resolveMonitoringActiveAgentKind(activatedSession.type));
      setMonitoringCardPanelFlow('manual-session');
      setIsNewMonitoringSelection(false);
      setActiveAgentSession(activatedSession);
      setSessionId(activatedSession.id);

      if (isMonitoringSessionProcessingStatus(activatedSession.status)) {
        launchMonitoringGeneratingUI(activatedSession, nextSelectedCards);
        if (shouldUseMonitoringMock(activatedSession.type)) {
          scheduleMonitoringMockCompletion(activatedSession.id, activatedSession.type);
        }
        return;
      }

      if (isMonitoringSessionReadyForResults(activatedSession)) {
        openMonitoringResults(activatedSession);
        return;
      }

      setSelectedAICards(nextSelectedCards);
      setOpenAICardPanel(true);
    } catch (error) {
      toast.error(extractApiErrorMessage(error) || t('notifications.valueChainStartSessionError'));
    }
  }, [
    activateAISession,
    getSelectedCardsFromSession,
    launchMonitoringGeneratingUI,
    openMonitoringResults,
    resetMonitoringSessionFlowPanels,
    scheduleMonitoringMockCompletion,
    setSelectedAICards,
    t,
  ]);

  // The assistant opens a monitoring session of any agent. Results: the run history panel on the
  // session, as its "View results" does, on the execution the chat's start made; the socket is
  // opened so the runs fill in as they end. Otherwise the session in the state it is in, as a
  // click in the Sessions list: its unit selection before the start.
  const onOpenMonitoringSessionFromAssistant = useCallback((
    session: ISessionVCItem,
    { view, executionKey }: TMonitoringSessionOpenRequest,
  ) => {
    onResetImpactScenariosView();

    if (view === 'results') {
      setIsAISocketLaunchRequested(true);
      openMonitoringResults(session, executionKey);
      return;
    }

    onEditMonitoringSession(session).catch(() => {});
  }, [onEditMonitoringSession, onResetImpactScenariosView, openMonitoringResults]);

  useValueChainMonitoringSessionAiAction({
    valueChainMapId,
    agentSessions: vcaAgentSessions?.data,
    isAgentSessionsFetching: isVCAgentSessionsFetching,
    agentSessionsFulfilledTimeStamp,
    refetchAgentSessions: refetchVCAgentSessions,
    isBoardReady: isInitialViewportReady,
    onOpenSession: onOpenMonitoringSessionFromAssistant,
  });

  const onDeleteMonitoringSession = useCallback((sessionItem: ISessionVCItem) => {
    if (!valueChainMapId) {
      return;
    }

    deleteSession({
      valueChainMapId,
      sessionId: sessionItem.id,
    })
      .unwrap()
      .then(() => {
        const isDeletedSessionActive = sessionId === sessionItem.id
          || activeAgentSession?.id === sessionItem.id;

        if (isDeletedSessionActive) {
          setSessionId(undefined);
          setActiveAgentSession(undefined);
          setSelectedAICards([]);
          setOpenAICardPanel(false);
          setOpenMonitoringHubSidebar(true);
        }

        toast.success(t('valueChainMap.aiSessionsDeleted'));
        refetchVCAgentSessions();
      })
      .catch((error) => {
        toast.error(extractApiErrorMessage(error) || t('common.unknownError'));
      });
  }, [
    activeAgentSession?.id,
    deleteSession,
    refetchVCAgentSessions,
    sessionId,
    setSelectedAICards,
    t,
    valueChainMapId,
  ]);

  useEffect(() => {
    if (!isProfitCentreModalOpen && !currentTargetSwimlaneId) {
      setNodes((prevNodes) => prevNodes.map((prevNode) => ({
        ...prevNode,
        data: {
          ...prevNode.data,
          isDragTarget: false,
          isValidSectionHighlight: false,
          isInvalidDropTarget: false,
          dropZoneType: undefined,
          draggingNodeType: undefined,
          tempHeight: undefined,
          tempYOffset: undefined,
          tempTotalChildrenHeight: undefined,
        },
      })));
    }
  }, [isProfitCentreModalOpen, currentTargetSwimlaneId, setNodes]);

  useEffect(() => {
    setNodes((prevNodes) => applyViewportCullingToDisplayNodes(
      applyCanvasUnitStateToNodes(prevNodes),
    ));
  }, [applyCanvasUnitStateToNodes, applyViewportCullingToDisplayNodes, setNodes]);

  useEffect(() => {
    setNodes((prevNodes) => {
      let hasChanges = false;
      const nextOnDeleteDraftUnit = isDraftReviewMode ? onDeleteDraftUnit : undefined;
      const shouldShowDraftDeleteButton = activeDraftSession?.subType
        !== VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE;

      const nextNodes = prevNodes.map((prevNode) => {
        if (prevNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
          return prevNode;
        }

        const nodeData = prevNode.data as unknown as ICustomUnitNodeData;
        if (
          nodeData.onDeleteDraftUnit === nextOnDeleteDraftUnit
          && nodeData.showDraftDeleteButton === shouldShowDraftDeleteButton
        ) {
          return prevNode;
        }

        hasChanges = true;
        return {
          ...prevNode,
          data: {
            ...nodeData,
            onDeleteDraftUnit: nextOnDeleteDraftUnit,
            showDraftDeleteButton: shouldShowDraftDeleteButton,
          } as unknown as Record<string, unknown>,
        };
      });

      return hasChanges ? nextNodes : prevNodes;
    });
  }, [activeDraftSession?.subType, isDraftReviewMode, nodes, onDeleteDraftUnit, setNodes]);

  const [isHintDismissed, setIsHintDismissed] = useState(false);
  const [isDiscardDraftConfirmOpen, setIsDiscardDraftConfirmOpen] = useState(false);

  const isReviewGeneratedHeaderVisible = isDraftReviewMode && (
    existingSession?.subType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE
    || existingSession?.subType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE
  );
  const isSupplierDiscoveryReview = existingSession?.subType
    === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE;
  const shouldShowAIGeneratingFloatingButton = (
    hasAIGeneratingSessions || isBomDraftFloatingButtonVisible
  ) && (
    !isReviewGeneratedHeaderVisible || isBomDraftFloatingButtonVisible
  );
  const isAIGeneratingModalOpen = openAIGeneratingModal && !openAICreatorBoMUnits && !openAICreatorSupplierUnits;
  const isAIGeneratingHintVisible = Boolean(aiGeneratingButtonHint) && !isHintDismissed;

  const isInitialLayoutPending = !isInitialViewportReady && nodes.length > 0;
  const shouldShowLoader = isValueChainLoading
    || isBoardFetching
    // Board queries are skipped while saved filters resolve, so isBoardFetching is
    // false during the gate — without this the map would show empty and idle.
    || isRestorePending
    || isInitialLayoutPending
    || Boolean(isBoardContentHidden)
    || isBoardGroupsLoading
    || isProfitCenterTuplesLoading
    || isGroupSwitchRendering;
  const styledEdges = useMemo(
    () => (isGroupingMode ? edges : updateEdgeStyles(edges)),
    [edges, isGroupingMode, updateEdgeStyles],
  );

  useEffect(() => {
    setIsHintDismissed(false);
  }, [aiGeneratingButtonHint]);

  useEffect(() => {
    if (!isGroupSwitchRendering) {
      return undefined;
    }

    const isTargetDataReady = !isValueChainLoading
      && !isBoardFetching
      && !isBoardGroupsLoading
      && !isProfitCenterTuplesLoading;

    if (!isTargetDataReady) {
      return undefined;
    }

    groupSwitchFrameRef.current = window.requestAnimationFrame(() => {
      groupSwitchFrameRef.current = window.requestAnimationFrame(() => {
        setIsGroupSwitchRendering(false);
      });
    });

    return () => {
      window.cancelAnimationFrame(groupSwitchFrameRef.current);
    };
  }, [
    isBoardFetching,
    isBoardGroupsLoading,
    isGroupSwitchRendering,
    isProfitCenterTuplesLoading,
    isValueChainLoading,
  ]);

  const handleMoveEnd = () => {
    onMoveEnd(getZoom());
  };

  const handleOpenAIGeneratingModal = () => {
    if (aiGeneratingButtonHint) {
      setIsHintDismissed(true);
    }

    onOpenAIGeneratingModal();
  };

  const handleOpenDiscardDraftConfirm = () => {
    setIsDiscardDraftConfirmOpen(true);
  };

  const handleRejectDiscardDraftConfirm = () => {
    setIsDiscardDraftConfirmOpen(false);
  };

  const handleConfirmDiscardDraft = () => {
    onDiscardDraftResult();
    setIsDiscardDraftConfirmOpen(false);
  };

  const handleChangeGroupBy = (newType: TBoardInstanceGroupType) => {
    if (newType === groupByType) {
      return;
    }

    setIsGroupSwitchRendering(true);
    setGroupByType(newType);
  };

  return (
    <ValueChainLocationsProvider countryLookup={countryLookup}>
      <ValueChainView
        viewportController={(
          <ValueChainViewportController
            boardData={boardData}
            isScenarioViewMode={isScenarioViewMode}
            isInitialViewportReady={isInitialViewportReady}
            openAICardPanel={openAICardPanel}
            isNodeDraggingRef={isNodeDraggingRef}
            applyViewportCullingToDisplayNodes={applyViewportCullingToDisplayNodes}
            setNodes={setNodes}
            onViewportSizeReady={handleViewportSizeReady}
            onViewportSync={handleViewportSync}
            onViewportSizeSync={handleViewportSizeSync}
            onGroupedDataSync={handleGroupedDataSync}
          />
        )}
        onMoveEnd={handleMoveEnd}
        isInitialViewportReady={isInitialViewportReady}
        nodes={nodes}
        styledEdges={styledEdges}
        onConnect={isBoardEditingEnabled ? onConnect : noop}
        onEdgeDoubleClick={isBoardEditingEnabled ? onEdgeDoubleClick : noop}
        onEdgeClick={isBoardEditingEnabled ? onEdgeClick : noop}
        onNodesChange={isNodeEditingEnabled ? onNodesChange : noop}
        onEdgesChange={isEdgeChangesEnabled ? onEdgesChange : noop}
        onNodeDoubleClick={isNodeEditingEnabled ? onNodeDoubleClick : noop}
        isDraftReviewMode={isDraftReviewMode}
        isDiscardDraftConfirmOpen={isDiscardDraftConfirmOpen}
        onOpenDiscardDraftConfirm={handleOpenDiscardDraftConfirm}
        onRejectDiscardDraftConfirm={handleRejectDiscardDraftConfirm}
        onConfirmDiscardDraft={handleConfirmDiscardDraft}
        isSupplierDiscoveryReview={isSupplierDiscoveryReview}
        onUseDraftResult={onUseDraftResult}
        isUseDraftResultDisabled={isUseDraftResultDisabled}
        draftCardToDeleteName={draftCardToDeleteName}
        isDraftDeleteModalOpen={isDraftDeleteModalOpen}
        onConfirmDraftDelete={onConfirmDraftDelete}
        onRejectDraftDelete={onRejectDraftDelete}
        isDraftDeleteLoading={isDeleteDraftSessionUnitsLoading}
        shouldShowLoader={shouldShowLoader}
        isInitialLayoutPending={isInitialLayoutPending}
        isBoardContentHidden={isBoardContentHidden}
        onNodeDrag={isNodeEditingEnabled ? onNodeDrag : noop}
        onNodeDragStart={isNodeEditingEnabled ? onNodeDragStart : noop}
        onNodeDragStop={isNodeEditingEnabled ? onNodeDragStop : noop}
        selectedBoard={selectedBoard}
        activeModal={isBoardEditingEnabled ? activeModal : undefined}
        closeModal={closeModal}
        onRenameSubmit={onRenameSubmit}
        onDrop={isNodeEditingEnabled ? onDrop : noop}
        onDragOver={isNodeEditingEnabled ? onDragOver : noop}
        onAddSwimlane={isBoardEditingEnabled ? onAddSwimlane : noop}
        isScenarioViewMode={isScenarioViewMode}
        isFullScreenActive={isFullScreenActive}
        isAccessDisabled={isAccessDisabled}
        isDeleteModalOpen={isDeleteModalOpen}
        setDeleteModalOpen={setDeleteModalOpen}
        onDeleteSwimlaneConfirm={onDeleteSwimlaneConfirm}
        enumData={enumData || {}}
        onAddServiceConnection={onAddServiceConnection}
        selectedEdge={selectedEdge || []}
        onOpenFilters={handleOpenFilters}
        openFilters={openFilters}
        openImpactScenariosSidebar={openImpactScenariosSidebar}
        onOpenImpactScenariosSidebar={onOpenImpactScenariosSidebar}
        onCloseImpactScenariosSidebar={onCloseImpactScenariosSidebar}
        selectedImpactAnalysis={selectedImpactAnalysis}
        onBackToImpactScenariosSidebar={handleBackToImpactScenarios}
        impactScenarios={impactScenarios}
        isImpactScenariosLoading={isImpactScenariosLoading}
        isImpactScenarioCreating={isImpactScenarioCreating}
        isImpactScenariosDeleting={isImpactScenariosDeleting}
        isImpactScenarioUpdating={isImpactScenarioUpdating}
        onCreateImpactScenario={handleCreateImpactScenario}
        onOpenImpactAnalysis={handleOpenImpactAnalysis}
        onRenameImpactAnalysis={handleRenameImpactAnalysis}
        onDeleteImpactScenario={onDeleteImpactScenario}
        boardEntities={boardEntities}
        isCreateScenarioSaving={isCreateScenarioSaving}
        onSaveScenario={handleSaveScenario}
        onUpdateScenario={handleUpdateAnalysisScenario}
        onDeleteAnalysisScenario={onDeleteAnalysisScenario}
        valueChainMapAnalysisById={valueChainMapAnalysisById}
        isAnalysisDetailsLoading={isAnalysisDetailsLoading}
        valueChainMapAnalysisResults={valueChainMapAnalysisResults}
        isAnalysisResultsLoading={isAnalysisResultsLoading}
        hasMoreAnalysisResults={hasMoreAnalysisResults}
        valueChainMapAnalysisScenarios={valueChainMapAnalysisScenarios}
        isAnalysisScenariosLoading={isAnalysisScenariosLoading}
        hasMoreAnalysisScenarios={hasMoreAnalysisScenarios}
        isCopyAnalysisResultLoading={isCopyAnalysisResultLoading}
        isCopyAnalysisScenariosLoading={isCopyAnalysisScenariosLoading}
        isDeleteAnalysisResultLoading={isDeleteAnalysisResultLoading}
        onLoadAnalysisResults={handleLoadAnalysisResults}
        onLoadMoreAnalysisResults={handleLoadMoreAnalysisResults}
        onLoadAnalysisScenarios={handleLoadAnalysisScenarios}
        onLoadMoreAnalysisScenarios={handleLoadMoreAnalysisScenarios}
        onCopyAnalysisResult={handleCopyAnalysisResult}
        onCopyAnalysisScenarios={handleCopyAnalysisScenarios}
        onDeleteAnalysisResult={handleDeleteAnalysisResult}
        isStartImpactAnalysisLoading={isStartImpactAnalysisLoading}
        onStartImpactAnalysis={handleStartImpactAnalysis}
        followedResultId={followedResultId}
        openRequestId={openRequestId}
        openResultId={openResultId}
        activeScenarioId={selectedScenarioId}
        onSelectScenario={handleSelectScenario}
        activeViewScenarioId={activeScenarioId}
        onViewScenario={handleViewScenario}
        activeViewResultId={activeResultId}
        onViewResult={handleViewResult}
        pendingScenarioInitialData={pendingScenarioInitialData}
        onClearPendingScenarioInitialData={handleClearPendingScenarioInitialData}
        openAIAgentsSidebar={openAIAgentsSidebar}
        openAISessionsSidebar={openAISessionsSidebar}
        openMonitoringHubSidebar={openMonitoringHubSidebar}
        activeAgentKind={activeAgentKind}
        onOpenAIAgentsSidebar={onOpenAIAgentsSidebar}
        onCloseAIAgentsSidebar={onCloseAIAgentsSidebar}
        onCloseAISessionsSidebar={onCloseAISessionsSidebar}
        openAICardPanel={openAICardPanel}
        onOpenSupplierDiscoveryPanel={onOpenSupplierDiscoveryPanel}
        onOpenReputationMonitoring={onOpenReputationMonitoring}
        onOpenRegulationMonitoring={onOpenRegulationMonitoring}
        onOpenFinanceMonitoring={onOpenFinanceMonitoring}
        onOpenOwnershipMonitoring={onOpenOwnershipMonitoring}
        onOpenClimateMonitoring={onOpenClimateMonitoring}
        onBackToAIAgentsSidebar={onBackToAIAgentsSidebar}
        onBackToAIAgentsFromMonitoringHub={onBackToAIAgentsFromMonitoringHub}
        onBackToMonitoringHubFromCardPanel={onBackToMonitoringHubFromCardPanel}
        onStartMonitoringSession={onStartMonitoringSession}
        onBackToAISessionsSidebar={onBackToAISessionsSidebar}
        onCreateAISessionSelection={onCreateAISessionSelection}
        isNewNTierSelection={isNewNTierSelection}
        isNewMonitoringSelection={isNewMonitoringSelection}
        onCreateMonitoringSessionSelection={onCreateMonitoringSessionSelection}
        onCreateMonitoringScheduleSelection={onCreateMonitoringScheduleSelection}
        onContinueToSchedule={onContinueToSchedule}
        onBackFromScheduleConfig={onBackFromScheduleConfig}
        onCloseScheduleConfigPanel={onCloseScheduleConfigPanel}
        onSaveMonitoringSchedule={onSaveMonitoringSchedule}
        onAgentConfigIdChange={setMonitoringAgentConfigId}
        onNTierSubTypeChange={setNTierLaneSubType}
        monitoringAgentConfigId={monitoringAgentConfigId}
        valueChainMapId={parsedValueChainMapId}
        openScheduleConfigPanel={openScheduleConfigPanel}
        monitoringCardPanelFlow={monitoringCardPanelFlow}
        monitoringHubActiveTab={monitoringHubActiveTab}
        onMonitoringHubActiveTabChange={setMonitoringHubActiveTab}
        monitoringSchedules={monitoringSchedules}
        isMonitoringSchedulesLoading={isMonitoringSchedulesFetching}
        isMonitoringSchedulesLoadFailed={isMonitoringSchedulesLoadFailed}
        isCreateMonitoringScheduleLoading={
          isCreateMonitoringScheduleLoading || isUpdateMonitoringScheduleLoading
        }
        isDeleteMonitoringScheduleLoading={
          isDeleteMonitoringScheduleLoading
          || isPauseMonitoringScheduleLoading
          || isResumeMonitoringScheduleLoading
        }
        openScheduleRunHistoryPanel={openScheduleRunHistoryPanel}
        activeMonitoringSchedule={activeMonitoringSchedule}
        editingMonitoringSchedule={editingMonitoringSchedule}
        onOpenMonitoringSchedule={onOpenMonitoringSchedule}
        onEditMonitoringSchedule={onEditMonitoringSchedule}
        onDeleteMonitoringSchedule={onDeleteMonitoringSchedule}
        onPauseMonitoringSchedule={onPauseMonitoringSchedule}
        onResumeMonitoringSchedule={onResumeMonitoringSchedule}
        onRunMonitoringScheduleNow={onRunMonitoringScheduleNow}
        isRunMonitoringScheduleNowLoading={isRunMonitoringScheduleNowLoading}
        onBackFromScheduleRunHistory={onBackFromScheduleRunHistory}
        onCloseScheduleRunHistoryPanel={onCloseScheduleRunHistoryPanel}
        onEditAISession={onEditAISession}
        onEditMonitoringSession={onEditMonitoringSession}
        onDeleteAISession={onDeleteAISession}
        onDeleteMonitoringSession={onDeleteMonitoringSession}
        onCloseAICardPanel={onCloseAICardPanel}
        onAnalyzeBom={onAnalyzeBom}
        hasSupplierNTierAttention={hasSupplierNTierAttention}
        hasReputationMonitoringAttention={hasReputationMonitoringAttention}
        hasRegulationMonitoringAttention={hasRegulationMonitoringAttention}
        hasFinanceMonitoringAttention={hasFinanceMonitoringAttention}
        hasOwnershipMonitoringAttention={hasOwnershipMonitoringAttention}
        hasClimateMonitoringAttention={hasClimateMonitoringAttention}
        monitoringAgentDefinition={activeMonitoringAgent}
        monitoringAgentTitleKey={activeMonitoringAgent.titleKey}
        existingMonitoringSession={existingMonitoringSession}
        effectiveMonitoringSession={effectiveMonitoringSession}
        openMonitoringResultsPanel={openMonitoringResultsPanel}
        monitoringResultsSessionTitle={monitoringResultsSessionTitle}
        monitoringResultsSessionId={monitoringResultsSessionId}
        monitoringResultsExecutionKey={monitoringResultsExecutionKey}
        monitoringResultsRunId={monitoringResultsRunId}
        monitoringResultsBreadcrumbMode={monitoringResultsBreadcrumbMode}
        monitoringResultsScheduleTitle={monitoringResultsScheduleTitle}
        monitoringResultsRunLabel={monitoringResultsRunLabel}
        monitoringResultsValueChainMapId={parsedValueChainMapId}
        monitoringResultsFallbackEntities={monitoringResultsFallbackEntities}
        showMonitoringImpactScenarioCta={showMonitoringImpactScenarioCta}
        isMonitoringImpactScenarioCreating={isImpactScenarioCreating}
        onCreateImpactScenarioFromMonitoringResults={onCreateImpactScenarioFromMonitoringResults}
        onBackFromMonitoringResultsPanel={onBackFromMonitoringResultsPanel}
        onCloseMonitoringResultsPanel={onCloseMonitoringResultsPanel}
        onViewMonitoringSessionResults={onViewMonitoringSessionResults}
        selectedAICards={selectedAICards}
        aiSessions={visibleAISessions}
        monitoringSessions={visibleMonitoringSessions}
        isAISessionsLoading={isVCAgentSessionsFetching || isAISessionOpeningInEditMode}
        isAISessionsLoadFailed={isVCAgentSessionsLoadFailed}
        isAISessionActionLoading={
        isAISessionOpeningInEditMode
        || isActivateAgentSessionLoading
        || isDeleteSessionLoading
      }
        aiGeneratingButtonHint={aiGeneratingButtonHint}
        shouldShowAIGeneratingFloatingButton={shouldShowAIGeneratingFloatingButton}
        isAIGeneratingHintVisible={isAIGeneratingHintVisible}
        isAIGeneratingModalOpen={isAIGeneratingModalOpen}
        isMonitoringGeneratingOverview={isMonitoringGeneratingOverview}
        generatingMonitoringSessions={generatingMonitoringSessions}
        groupByType={groupByType}
        isGroupingMode={isGroupingMode}
        onChangeGroupBy={handleChangeGroupBy}
        onOpenAIGeneratingModal={handleOpenAIGeneratingModal}
        onCloseAIGeneratingModal={onCloseAIGeneratingModal}
        openAICreatorBoMUnits={openAICreatorBoMUnits}
        onCloseAICreatorBoMUnits={onCloseAICreatorBoMUnits}
        openAICreatorSupplierUnits={openAICreatorSupplierUnits}
        onCloseAICreatorSupplierUnits={onCloseAICreatorSupplierUnits}
        onOpenGeneratingSession={onOpenGeneratingSession}
        bomUnitsCards={bomUnitsCards}
        bomUnitsInitialTabNodeId={bomUnitsInitialTabNodeId}
        availableBoMManufacturerCards={availableBoMManufacturerCards}
        bomUnitsSession={liveBomUnitsSession}
        supplierUnitsCards={supplierUnitsCards}
        supplierUnitsInitialTabNodeId={supplierUnitsInitialTabNodeId}
        supplierUnitsSession={liveSupplierUnitsSession}
        agentSessionsFulfilledTimeStamp={agentSessionsFulfilledTimeStamp}
        refetchAgentSessions={refetchVCAgentSessions}
        generatingAICards={generatingAICards}
        generatingAISessions={generatingAISessions}
        onBoMGenerateWithAI={onBoMGenerateWithAI}
        onBoMAddRecord={onBoMAddRecord}
        onBoMAddManufacturer={onBoMAddManufacturer}
        onBoMConfirm={onBoMConfirm}
        onBoMProcessingTabsChange={onBoMProcessingTabsChange}
        onSupplierGenerateWithAI={onSupplierGenerateWithAI}
        onSupplierAddRecord={onSupplierAddRecord}
        onSupplierConfirm={onSupplierConfirm}
        onSupplierProcessingTabsChange={onSupplierProcessingTabsChange}
        onSearchSuppliersSuccess={onSearchSuppliersSuccess}
        allAvailableAICards={allAvailableAICards}
        onSyncSelectedAICardsFromSession={onSyncSelectedAICardsFromSession}
        onRemoveSelectedAICard={onRemoveSelectedAICard}
        onToggleSelectedAICard={onToggleAIPanelCardSelection}
        onResetSelectedAICards={onResetSelectedAICards}
        filtersData={filtersData}
        orgStructureData={orgStructureData?.data}
        isSelectedFilter={!!requestFilters}
        onApplyFilters={onApplyFilters}
        districtErrors={districtErrors}
        selectedCurrentFilters={selectedCurrentFilters}
        setSelectedCurrentFilters={setSelectedCurrentFilters}
        followConnections={followConnections}
        onToggleFollowConnections={onToggleFollowConnections}
        sessionId={sessionId}
        setSessionId={setSessionId}
        existingSession={existingSession}
        isVCAgentSessionsLoaded={isVCAgentSessionsLoaded}
        isVCAgentSessionsLoadFailed={isVCAgentSessionsLoadFailed}
        isAIDraftUnitsReadyForSelectionSync={isAIDraftUnitsReadyForSelectionSync}
        handleCloseModalBom={handleCloseModalBom}
        handleCloseModalSupplier={handleCloseModalSupplier}
        isBomSocketConnected={isBomSocketConnected}
        sendBomSocketUnitUpdated={sendBomSocketUnitUpdated}
        lastBomSocketPayload={lastBomSocketPayload}
        isSocketPollingFallbackActive={isSocketPollingFallbackActive}
      />
    </ValueChainLocationsProvider>
  );
}

export default ValueChain;
